import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { axiosPrivate } from "../../../api/axios";
import API_URL from "../../../utils/API_URL";
import resultGraphSchema from "./DB_SCHEMA/result_graph/webprodis_db_schema_result_graph.json";

const RESULT_GROUPS = [
  {
    id: "process",
    label: "공정 지표 (리드타임/라인밸런싱/택타임)",
    description: "LeadTime, Line Balancing, TaktTime",
  },
  {
    id: "utilization",
    label: "가동실적",
    description: "장비/물류/작업자 가동실적 및 UPH",
  },
  {
    id: "flow",
    label: "흐름/병목",
    description: "Buffer, 혼잡도",
  },
];

const RESULT_GROUP_BY_TABLE = {
  sim_res_proc_leadtime: "process",
  sim_res_linebalancing: "process",
  sim_res_takttime: "process",
  sim_res_utilization_mch: "utilization",
  sim_res_utilization_logistics: "utilization",
  sim_res_utilization_worker: "utilization",
  sim_res_uph: "utilization",
  sim_res_mch_buffer: "flow",
  sim_res_logistics_congestion: "flow",
};

const RESULT_RENDER_TYPE_BY_TABLE = {
  sim_res_proc_leadtime: "procleadtime",
  sim_res_linebalancing: "linebalancing",
  sim_res_takttime: "table",
  sim_res_utilization_mch: "chart",
  sim_res_utilization_logistics: "chart",
  sim_res_utilization_worker: "chart",
  sim_res_mch_buffer: "table",
  sim_res_uph: "chart",
  sim_res_logistics_congestion: "chart",
};

const DEFAULT_LEFT_SIMULATION_OPTION_ID = "simulation-1";
const DEFAULT_RIGHT_SIMULATION_OPTION_ID = "simulation-2";

const resolveResultRenderType = (tableName, tableDesc = "") => {
  if (RESULT_RENDER_TYPE_BY_TABLE[tableName]) {
    return RESULT_RENDER_TYPE_BY_TABLE[tableName];
  }

  return tableDesc.includes("그래프") ? "chart" : "table";
};

const PLACEHOLDER_BAR_VALUES = [22, 48, 37, 58, 44, 68, 39, 61, 54];
const PLACEHOLDER_ROW_KEYS = [
  "row-1",
  "row-2",
  "row-3",
  "row-4",
  "row-5",
  "row-6",
];
const TAKT_TIME_PREFERRED_COLUMNS = [
  "order_name",
  "proc_name",
  "mch_quantity",
  "result_cnt",
  "proc_time",
  "takt_time",
];
const TAKT_TIME_COLUMN_LABELS = {
  order_name: "주문명",
  proc_name: "공정명",
  mch_quantity: "장비 수",
  result_cnt: "공정 처리 횟수",
  proc_time: "평균 공정시간 (sec)",
  takt_time: "평균 TaktTime (sec)",
};
const UTILIZATION_RATE_COLOR_BY_KEY = {
  util_rate: "#34d399",
  failure_rate: "#f87171",
  defect_rate: "#fbbf24",
  transfer_rate: "#38bdf8",
  loading_rate: "#818cf8",
  retrieval_rate: "#a78bfa",
  empty_rate: "#22d3ee",
  processing_rate: "#60a5fa",
  pre_process_rate: "#22d3ee",
  process_rate: "#818cf8",
  post_process_rate: "#a78bfa",
  move_rate: "#60a5fa",
  worker_absence: "#f87171",
  buffer_cnt: "#38bdf8",
  buffer_min: "#22d3ee",
  buffer_avg: "#818cf8",
  buffer_max: "#a78bfa",
};
const UTILIZATION_FALLBACK_COLORS = [
  "#22d3ee",
  "#818cf8",
  "#a78bfa",
  "#34d399",
  "#fbbf24",
  "#f87171",
  "#60a5fa",
];
const TIME_SERIES_LINE_COLORS = [
  "#22d3ee",
  "#818cf8",
  "#34d399",
  "#f59e0b",
  "#f472b6",
  "#a78bfa",
  "#38bdf8",
  "#fb7185",
  "#2dd4bf",
  "#fbbf24",
];

const RESULT_FALLBACK_ITEMS = [
  { tableName: "sim_res_proc_leadtime", label: "공정간 LeadTime" },
  { tableName: "sim_res_linebalancing", label: "실적 Line Balancing 그래프" },
  { tableName: "sim_res_takttime", label: "Takt Time" },
  { tableName: "sim_res_utilization_mch", label: "장비별 가동실적 그래프" },
  { tableName: "sim_res_utilization_logistics", label: "물류 가동실적 그래프" },
  { tableName: "sim_res_utilization_worker", label: "작업자 작업실적 그래프" },
  { tableName: "sim_res_mch_buffer", label: "Buffer상태" },
  { tableName: "sim_res_uph", label: "UPH 그래프" },
  { tableName: "sim_res_logistics_congestion", label: "물류 혼잡 그래프" },
].map((item) => ({
  id: item.tableName,
  tableName: item.tableName,
  label: item.label,
  groupId: RESULT_GROUP_BY_TABLE[item.tableName] || "flow",
  renderType: resolveResultRenderType(item.tableName, item.label),
  description: "스키마 기반 결과 상세 UI 연결 예정",
  keyColumns: ["sim_id", "updated_at"],
}));

const buildResultItems = () => {
  const schemaTables = Array.isArray(resultGraphSchema?.tables)
    ? resultGraphSchema.tables
    : [];

  if (schemaTables.length === 0) {
    return RESULT_FALLBACK_ITEMS;
  }

  return schemaTables.map((table, index) => {
    const tableName = table.table_name || `sim_result_${index + 1}`;
    const keyColumns = Array.isArray(table.columns)
      ? table.columns
          .filter((column) => column.key)
          .map((column) => column.name)
          .slice(0, 4)
      : [];

    return {
      id: tableName,
      tableName,
      label: table.table_desc || tableName,
      groupId: RESULT_GROUP_BY_TABLE[tableName] || "flow",
      renderType: resolveResultRenderType(tableName, table.table_desc || ""),
      description: `${Array.isArray(table.columns) ? table.columns.length : 0}개 컬럼`,
      keyColumns,
    };
  });
};

const formatSimulationOptionLabel = (simulationInfo) => {
  if (!simulationInfo) {
    return "";
  }

  const simIdText = simulationInfo.simId
    ? `SimId ${simulationInfo.simId}`
    : "SimId -";
  const simNameText = simulationInfo.simName || "최신 시뮬레이션";
  const updateDateText = simulationInfo.updateDate
    ? new Date(simulationInfo.updateDate).toLocaleString("ko-KR")
    : "시간정보 없음";

  return `${simNameText} (${simIdText}) · ${updateDateText}`;
};

const buildDemoSimulationOptions = (latestSimulation) => {
  const latestSummary = formatSimulationOptionLabel(latestSimulation);
  const sourceText = latestSummary
    ? ` · ${latestSummary}`
    : " · 최신 결과 미저장(데모 템플릿)";

  return [
    {
      id: DEFAULT_LEFT_SIMULATION_OPTION_ID,
      label: `Simulation 1${sourceText}`,
      simId: latestSimulation?.simId || null,
      payload: latestSimulation || null,
      isDemo: true,
    },
    {
      id: DEFAULT_RIGHT_SIMULATION_OPTION_ID,
      label: `Simulation 2${sourceText}`,
      simId: latestSimulation?.simId || null,
      payload: latestSimulation || null,
      isDemo: true,
    },
  ];
};

function ResultChartPlaceholder({ title }) {
  return (
    <div className="rounded-xl border border-slate-700/70 bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950/30 p-4">
      <div className="mb-3 text-xs font-semibold text-slate-300">{title}</div>
      <div className="h-[220px] overflow-hidden rounded-lg border border-slate-800 bg-slate-950/70 px-3 py-3">
        <div className="flex h-full items-end gap-2">
          {PLACEHOLDER_BAR_VALUES.map((height) => (
            <div key={`bar-${height}`} className="flex h-full flex-1 items-end">
              <div
                className="w-full rounded-t bg-gradient-to-t from-indigo-500/70 to-cyan-300/80"
                style={{ height: `${height}%` }}
              />
            </div>
          ))}
        </div>
      </div>
      <p className="mt-2 text-[11px] text-slate-500">
        결과 그래프 렌더러 연동 전 템플릿입니다. 축/범례/툴팁은 데이터 연결
        단계에서 적용됩니다.
      </p>
    </div>
  );
}

function ResultTablePlaceholder({ columns }) {
  const tableColumns =
    columns.length > 0 ? columns : ["key_1", "key_2", "value_1", "value_2"];

  return (
    <div className="rounded-xl border border-slate-700/70 bg-slate-900/80 p-4">
      <div className="overflow-x-auto rounded-lg border border-slate-800 bg-slate-950/60 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-600 [&::-webkit-scrollbar-track]:bg-transparent">
        <table className="w-full min-w-[520px] table-auto border-collapse text-left text-xs">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/70 text-slate-300">
              {tableColumns.slice(0, 4).map((columnName) => (
                <th
                  key={columnName}
                  className="px-3 py-2 font-semibold uppercase tracking-[0.05em]"
                >
                  {columnName}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PLACEHOLDER_ROW_KEYS.map((rowKey) => (
              <tr
                key={rowKey}
                className="border-b border-slate-900/70 text-slate-400"
              >
                {tableColumns.slice(0, 4).map((columnName) => (
                  <td key={`${columnName}-${rowKey}`} className="px-3 py-2">
                    -
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-slate-500">
        실제 행 데이터는 결과 그래프 API 연결 후 표시됩니다.
      </p>
    </div>
  );
}

const formatTimeValue = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return "-";
  }

  return parsed.toLocaleString("ko-KR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatIntegerValue = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return "-";
  }
  return Math.round(parsed).toLocaleString("ko-KR");
};

const formatRateValue = (value, treatAsRatio) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return "-";
  }

  const normalized = treatAsRatio ? parsed * 100 : parsed;
  return normalized.toLocaleString("ko-KR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const getUtilizationRateColor = (rateKey, index) => {
  if (UTILIZATION_RATE_COLOR_BY_KEY[rateKey]) {
    return UTILIZATION_RATE_COLOR_BY_KEY[rateKey];
  }

  return UTILIZATION_FALLBACK_COLORS[
    index % UTILIZATION_FALLBACK_COLORS.length
  ];
};

const compareIdentifier = (leftValue, rightValue) => {
  const leftNumber = Number(leftValue);
  const rightNumber = Number(rightValue);

  const leftIsNumber = Number.isFinite(leftNumber);
  const rightIsNumber = Number.isFinite(rightNumber);

  if (leftIsNumber && rightIsNumber) {
    return leftNumber - rightNumber;
  }

  return String(leftValue).localeCompare(String(rightValue), "ko-KR", {
    numeric: true,
    sensitivity: "base",
  });
};

const buildUniqueProcessOptions = (records, procKey, fallbackPrefix) => {
  const processMap = new Map();

  for (const record of records) {
    const process = record?.[procKey] || null;
    const processId = String(process?.id ?? "").trim();
    if (!processId) {
      continue;
    }

    if (!processMap.has(processId)) {
      processMap.set(processId, {
        id: processId,
        name: process?.name || `${fallbackPrefix} ${processId}`,
      });
    }
  }

  return Array.from(processMap.values()).sort((leftOption, rightOption) => {
    return compareIdentifier(leftOption.id, rightOption.id);
  });
};

const buildChartTicks = (maxValue) => {
  if (maxValue <= 0) {
    return [{ id: "tick-zero", value: 0 }];
  }

  return [
    { id: "tick-max", value: maxValue },
    { id: "tick-75", value: maxValue * 0.75 },
    { id: "tick-50", value: maxValue * 0.5 },
    { id: "tick-25", value: maxValue * 0.25 },
    { id: "tick-min", value: 0 },
  ];
};

const getTimeSeriesLineColor = (index) => {
  return TIME_SERIES_LINE_COLORS[index % TIME_SERIES_LINE_COLORS.length];
};

const formatTimeAxisLabel = (value) => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  const parsedTime = Date.parse(String(value));
  if (Number.isNaN(parsedTime)) {
    return String(value);
  }

  return new Date(parsedTime).toLocaleTimeString("ko-KR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatTooltipDateTime = (value) => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  const parsedTime = Date.parse(String(value));
  if (Number.isNaN(parsedTime)) {
    return String(value);
  }

  return new Date(parsedTime).toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

function TaktTimeMetricContent({ metricData }) {
  const rows = useMemo(() => {
    return Array.isArray(metricData?.rows) ? metricData.rows : [];
  }, [metricData?.rows]);

  const pagination = metricData?.pagination || null;

  const columnsMeta = useMemo(() => {
    if (Array.isArray(metricData?.columns) && metricData.columns.length > 0) {
      return metricData.columns
        .filter((column) => column?.key)
        .map((column) => ({
          key: column.key,
          label:
            column.label || TAKT_TIME_COLUMN_LABELS[column.key] || column.key,
        }));
    }

    if (!rows.length) {
      return [];
    }

    const rowKeys = Object.keys(rows[0]);
    const selectedColumns = TAKT_TIME_PREFERRED_COLUMNS.filter((key) =>
      rowKeys.includes(key),
    );
    const extraColumns = rowKeys.filter(
      (key) => !selectedColumns.includes(key),
    );

    return [...selectedColumns, ...extraColumns].map((columnKey) => ({
      key: columnKey,
      label: TAKT_TIME_COLUMN_LABELS[columnKey] || columnKey,
    }));
  }, [metricData?.columns, rows]);

  const formatTaktCellValue = useCallback((columnKey, value) => {
    if (columnKey === "proc_time" || columnKey === "takt_time") {
      return formatTimeValue(value);
    }

    if (columnKey === "mch_quantity" || columnKey === "result_cnt") {
      return formatIntegerValue(value);
    }

    if (value === undefined || value === null || value === "") {
      return "-";
    }

    return String(value);
  }, []);

  if (!rows.length) {
    return (
      <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/60 px-4 py-8 text-center text-xs text-slate-500">
        Takt Time 데이터가 없습니다.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-700/70 bg-slate-900/80 p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="rounded-md border border-indigo-500/30 bg-indigo-500/10 px-2 py-1 text-[10px] font-semibold text-indigo-200">
          정렬 기준: 주문 ID → 공정 ID
        </div>
        {pagination && (
          <div className="text-[10px] text-slate-500">
            표시 {pagination.returnedCount} / 전체 {pagination.totalCount}
          </div>
        )}
      </div>

      <div
        className="overflow-x-auto overflow-y-auto rounded-lg border border-slate-800 bg-slate-950/70 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-600 [&::-webkit-scrollbar-track]:bg-transparent"
        style={{ maxHeight: "320px" }}
      >
        <table className="w-full min-w-[760px] table-auto border-collapse text-left text-xs">
          <thead>
            <tr
              className="border-b border-slate-800 text-slate-300"
              style={{ background: "rgb(15,23,42)" }}
            >
              {columnsMeta.map((column) => (
                <th
                  key={`takttime-head-${column.key}`}
                  className="px-3 py-2 font-semibold uppercase tracking-[0.05em]"
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "rgb(15,23,42)",
                    zIndex: 1,
                  }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const rowKey = `${row?.order_name ?? "-"}|${row?.proc_name ?? "-"}|${rowIndex}`;

              return (
                <tr
                  key={`takttime-row-${rowKey}`}
                  className="border-b border-slate-900/70 text-slate-300 last:border-b-0"
                >
                  {columnsMeta.map((column) => (
                    <td
                      key={`takttime-cell-${rowKey}-${column.key}`}
                      className="px-3 py-2"
                    >
                      {formatTaktCellValue(column.key, row[column.key])}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UtilizationMetricContent({ metricData, metricTitle, isPercentMetric = true }) {
  const outerRef = useRef(null);
  const [containerPx, setContainerPx] = useState(0);
  const [tooltip, setTooltip] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [hiddenRateKeys, setHiddenRateKeys] = useState(new Set());

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => {
      setContainerPx(Math.floor(entry.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rows = useMemo(() => Array.isArray(metricData?.rows) ? metricData.rows : [], [metricData?.rows]);
  const rateColumns = useMemo(() => Array.isArray(metricData?.rateColumns) ? metricData.rateColumns : [], [metricData?.rateColumns]);

  if (!rows.length || !rateColumns.length) {
    return (
      <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/60 px-4 py-8 text-center text-xs text-slate-500">
        {metricTitle} 데이터가 없습니다.
      </div>
    );
  }

  const maxRawRate = rows.reduce((maxValue, row) => {
    return rateColumns.reduce((columnMaxValue, rateColumn) => {
      const parsedValue = Number(row?.rates?.[rateColumn.key]);
      const safeValue = Number.isFinite(parsedValue) ? parsedValue : 0;
      return safeValue > columnMaxValue ? safeValue : columnMaxValue;
    }, maxValue);
  }, 0);

  const treatAsRatio = isPercentMetric && maxRawRate <= 1.5;

  const displayedRows = rows.map((row) => {
    const displayRates = {};
    for (const rateColumn of rateColumns) {
      const rawValue = Number(row?.rates?.[rateColumn.key]);
      const safeRawValue = Number.isFinite(rawValue) ? rawValue : 0;
      displayRates[rateColumn.key] = treatAsRatio && isPercentMetric ? safeRawValue * 100 : safeRawValue;
    }
    return { ...row, displayRates };
  });

  const maxDisplayRate = displayedRows.reduce((maxValue, row) => {
    return rateColumns.reduce((columnMaxValue, rateColumn) => {
      const parsedValue = Number(row?.displayRates?.[rateColumn.key]);
      const safeValue = Number.isFinite(parsedValue) ? parsedValue : 0;
      return safeValue > columnMaxValue ? safeValue : columnMaxValue;
    }, maxValue);
  }, 0);

  const chartMax = isPercentMetric ? Math.max(100, maxDisplayRate, 1) : Math.max(maxDisplayRate, 1);
  const chartTicks = buildChartTicks(chartMax);
  const valueSuffix = isPercentMetric ? "%" : "";
  const pct100Bottom = isPercentMetric ? 46 + Math.min((100 / chartMax), 1) * 220 : null;

  const CHART_H = 220;
  const Y_COL_W = 66;
  const GROUP_W = Math.max(108, rateColumns.length * 22 + 24);
  const barWidth = Math.max(8, Math.min(18, Math.floor((GROUP_W - 24) / Math.max(rateColumns.length, 1)) - 2));
  const totalWidth = Y_COL_W + rows.length * GROUP_W;
  const scrollBoxWidth = containerPx > 0 ? containerPx : null;

  const TOOLTIP_W = 210;
  const TOOLTIP_H = 52 + rateColumns.length * 22;

  const handleGroupMouseMove = (row, e) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    setTooltip({
      row,
      x: Math.min(e.clientX + 14, vw - TOOLTIP_W - 8),
      y: Math.min(e.clientY - 10, vh - TOOLTIP_H - 8),
    });
    setHoveredRowId(row.id);
  };

  const handleGroupMouseLeave = () => {
    setTooltip(null);
    setHoveredRowId(null);
  };

  const getUtilValueColor = (value) => {
    if (!isPercentMetric) return null;
    if (value >= 95) return "#f87171";
    if (value >= 80) return "#fbbf24";
    return "#34d399";
  };

  return (
    <>
      {/* ── Fixed-position Tooltip ── */}
      {tooltip && (
        <div style={{
          position: "fixed",
          left: tooltip.x,
          top: tooltip.y,
          zIndex: 9999,
          background: "rgba(2,6,23,0.97)",
          border: "1px solid rgba(99,102,241,0.5)",
          borderRadius: "10px",
          padding: "11px 14px",
          minWidth: `${TOOLTIP_W}px`,
          boxShadow: "0 12px 40px rgba(0,0,0,0.65), 0 0 0 1px rgba(99,102,241,0.12)",
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: "#f1f5f9", marginBottom: "2px" }}>
            {tooltip.row.name}
          </div>
          <div style={{ fontSize: "9px", color: "#475569", marginBottom: "8px", paddingBottom: "7px", borderBottom: "1px solid rgba(51,65,85,0.6)" }}>
            {tooltip.row.id}
          </div>
          {rateColumns.filter(col => !hiddenRateKeys.has(col.key)).map((col, idx) => {
            const val = Number(tooltip.row.displayRates?.[col.key]);
            const safeVal = Number.isFinite(val) ? val : 0;
            const barColor = getUtilizationRateColor(col.key, rateColumns.findIndex(c => c.key === col.key));
            const textColor = getUtilValueColor(safeVal) || barColor;
            return (
              <div key={`tip-${col.key}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", fontSize: "10px", marginBottom: "4px" }}>
                <span style={{ display: "flex", alignItems: "center", gap: "5px", color: "#64748b" }}>
                  <span style={{ width: "7px", height: "7px", borderRadius: "2px", background: barColor, display: "inline-block", flexShrink: 0 }} />
                  {col.label}
                </span>
                <span style={{ color: textColor, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                  {formatRateValue(safeVal, false)}{valueSuffix}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Chart card ── */}
      <div
        ref={outerRef}
        style={{ width: "100%", overflow: "hidden", borderRadius: "12px", border: "1px solid rgba(51,65,85,0.7)", background: "rgba(15,23,42,0.86)" }}
      >
        <style>{`
          .util-chart-scroll::-webkit-scrollbar { height: 7px; }
          .util-chart-scroll::-webkit-scrollbar-track { background: rgba(15,23,42,0.9); border-radius: 4px; }
          .util-chart-scroll::-webkit-scrollbar-thumb { background: #475569; border-radius: 4px; }
          .util-chart-scroll::-webkit-scrollbar-thumb:hover { background: #64748b; }
        `}</style>

        {/* Header / Legend */}
        <div style={{ padding: "10px 14px 0 14px", borderBottom: "1px solid rgba(30,41,59,0.9)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "7px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
              <span style={{ fontSize: "11px", fontWeight: 700, color: "#e2e8f0", letterSpacing: "0.02em" }}>
                {metricTitle}{isPercentMetric ? " (%)" : ""}
              </span>
              {isPercentMetric && (
                <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "9px", color: "#475569" }}>
                  <span style={{ display: "inline-block", width: "14px", height: "1px", borderTop: "1px dashed rgba(248,113,113,0.6)", verticalAlign: "middle" }} />
                  100%
                </span>
              )}
            </div>
            {hiddenRateKeys.size > 0 && (
              <button type="button" onClick={() => setHiddenRateKeys(new Set())}
                style={{ fontSize: "9px", color: "#6366f1", background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.3)", borderRadius: "6px", padding: "2px 7px", cursor: "pointer", outline: "none" }}>
                전체 표시
              </button>
            )}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", paddingBottom: "8px" }}>
            {rateColumns.map((rateColumn, index) => {
              const color = getUtilizationRateColor(rateColumn.key, index);
              const hidden = hiddenRateKeys.has(rateColumn.key);
              return (
                <button
                  key={`util-legend-${rateColumn.key}`}
                  type="button"
                  onClick={() => {
                    setHiddenRateKeys(prev => {
                      const next = new Set(prev);
                      if (next.has(rateColumn.key)) next.delete(rateColumn.key);
                      else next.add(rateColumn.key);
                      return next;
                    });
                  }}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "5px",
                    padding: "3px 9px 3px 7px", borderRadius: "99px",
                    border: `1px solid ${hidden ? "rgba(51,65,85,0.35)" : `${color}50`}`,
                    background: hidden ? "rgba(15,23,42,0.5)" : `${color}14`,
                    cursor: "pointer", opacity: hidden ? 0.45 : 1,
                    transition: "all 0.15s", outline: "none",
                  }}
                >
                  <span style={{ display: "inline-block", width: "10px", height: "7px", borderRadius: "2px", background: hidden ? "#334155" : `linear-gradient(135deg, ${color}88, ${color})`, flexShrink: 0 }} />
                  <span style={{ fontSize: "10px", color: hidden ? "#4b5563" : "#94a3b8", whiteSpace: "nowrap" }}>{rateColumn.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Scroll container */}
        <div
          className="util-chart-scroll"
          style={{
            overflowX: "auto", overflowY: "hidden",
            scrollbarWidth: "thin", scrollbarColor: "#475569 rgba(15,23,42,0.9)",
            ...(scrollBoxWidth ? { width: `${scrollBoxWidth}px` } : { width: "100%" }),
          }}
        >
          <div style={{ width: `${totalWidth}px` }}>
            <div style={{ display: "flex", height: `${CHART_H + 46}px`, background: "rgba(2,6,23,0.5)" }}>

              {/* Y-axis */}
              <div style={{
                width: `${Y_COL_W}px`, flexShrink: 0, height: `${CHART_H}px`,
                display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "flex-end",
                padding: "2px 10px 2px 0", borderRight: "1px solid rgba(51,65,85,0.4)",
                boxSizing: "border-box", background: "rgba(2,6,23,0.6)",
              }}>
                {chartTicks.map((tick) => (
                  <span key={`util-tick-${tick.id}`} style={{ fontSize: "9px", color: "#64748b", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                    {formatRateValue(tick.value, false)}
                  </span>
                ))}
              </div>

              {/* Bar group columns */}
              {displayedRows.map((row) => {
                const isHovered = hoveredRowId === row.id;
                return (
                  <div
                    key={`util-group-${row.id}-${row.name}`}
                    onMouseMove={(e) => handleGroupMouseMove(row, e)}
                    onMouseLeave={handleGroupMouseLeave}
                    style={{
                      width: `${GROUP_W}px`, flexShrink: 0,
                      height: `${CHART_H + 46}px`,
                      borderRight: "1px solid rgba(15,23,42,0.7)",
                      boxSizing: "border-box", position: "relative",
                      background: isHovered ? "rgba(99,102,241,0.05)" : "transparent",
                      transition: "background 0.15s ease",
                      cursor: "crosshair",
                    }}
                  >
                    {/* Grid lines */}
                    {chartTicks.map((tick) => {
                      const pct = Math.min((tick.value / chartMax) * 100, 100);
                      return (
                        <div key={`util-grid-${row.id}-${tick.id}`} style={{
                          position: "absolute", left: 0, right: "-1px",
                          bottom: `${46 + (pct / 100) * CHART_H}px`,
                          borderTop: "1px solid rgba(30,41,59,0.7)", pointerEvents: "none",
                        }} />
                      );
                    })}

                    {/* 100% 기준선 */}
                    {pct100Bottom !== null && (
                      <div style={{
                        position: "absolute", left: 0, right: "-1px",
                        bottom: `${pct100Bottom}px`,
                        borderTop: "1px dashed rgba(248,113,113,0.5)",
                        zIndex: 10, pointerEvents: "none",
                      }} />
                    )}

                    {/* Bars */}
                    <div style={{
                      height: `${CHART_H}px`,
                      display: "flex", alignItems: "flex-end", justifyContent: "center",
                      gap: "3px", padding: "0 10px", boxSizing: "border-box",
                      position: "relative", zIndex: 1,
                    }}>
                      {rateColumns.map((rateColumn, rateIndex) => {
                        if (hiddenRateKeys.has(rateColumn.key)) return null;
                        const displayValue = Number(row?.displayRates?.[rateColumn.key]);
                        const safeDisplayValue = Number.isFinite(displayValue) ? displayValue : 0;
                        const barHeight = Math.round(Math.min(safeDisplayValue / chartMax, 1) * CHART_H);
                        const color = getUtilizationRateColor(rateColumn.key, rateIndex);
                        return (
                          <div
                            key={`util-bar-${row.id}-${rateColumn.key}`}
                            style={{
                              width: `${barWidth}px`,
                              height: `${barHeight}px`,
                              minHeight: barHeight > 0 ? "2px" : "0px",
                              borderRadius: "4px 4px 0 0",
                              background: isHovered
                                ? `linear-gradient(to top, ${color}cc, ${color}ff)`
                                : `linear-gradient(to top, ${color}88, ${color}dd)`,
                              boxShadow: isHovered && barHeight > 0
                                ? `0 0 14px ${color}55, 0 -2px 8px ${color}30`
                                : barHeight > 0 ? `0 0 6px ${color}22` : "none",
                              transition: "background 0.15s ease, box-shadow 0.15s ease",
                            }}
                          />
                        );
                      })}
                    </div>

                    {/* X-axis label */}
                    <div style={{
                      height: "46px",
                      display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
                      gap: "2px", padding: "3px 6px", boxSizing: "border-box",
                    }}>
                      <span style={{
                        maxWidth: "100%", fontSize: "10px", fontWeight: 700,
                        color: isHovered ? "#e2e8f0" : "#cbd5e1",
                        textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        transition: "color 0.15s ease",
                      }}>
                        {row.name}
                      </span>
                      <span style={{ fontSize: "9px", color: isHovered ? "#94a3b8" : "#475569", transition: "color 0.15s ease" }}>
                        {row.id}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function TimeSeriesMetricContent({
  metricData,
  metricTitle,
  valueLabel,
  valueUnit = "",
  emptyMessage,
}) {
  const outerRef = useRef(null);
  const [containerPx, setContainerPx] = useState(0);
  const [tooltip, setTooltip] = useState(null);
  const [hoveredXIndex, setHoveredXIndex] = useState(null);
  const [hiddenSeriesIds, setHiddenSeriesIds] = useState(new Set());

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => {
      setContainerPx(Math.floor(entry.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const xValues = useMemo(() => {
    return Array.isArray(metricData?.xAxis?.values) ? metricData.xAxis.values : [];
  }, [metricData?.xAxis?.values]);

  const rawSeries = useMemo(() => {
    return Array.isArray(metricData?.series) ? metricData.series : [];
  }, [metricData?.series]);

  if (!xValues.length || !rawSeries.length) {
    return (
      <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/60 px-4 py-8 text-center text-xs text-slate-500">
        {emptyMessage || `${metricTitle} 데이터가 없습니다.`}
      </div>
    );
  }

  const series = rawSeries.map((item, index) => {
    const color = getTimeSeriesLineColor(index);
    const points = Array.isArray(item?.points)
      ? item.points.map((point, pointIndex) => {
          const pointValue = Number(point?.value);
          return {
            timedata: point?.timedata || xValues[pointIndex] || "",
            value: Number.isFinite(pointValue) ? pointValue : null,
          };
        })
      : [];
    return {
      id: item?.id || String(index + 1),
      name: item?.name || item?.id || `Series ${index + 1}`,
      color,
      points,
    };
  });

  const visibleSeries = series.filter((s) => !hiddenSeriesIds.has(s.id));

  const maxVisibleValue = visibleSeries.reduce((maxValue, seriesItem) => {
    return seriesItem.points.reduce((pointMax, point) => {
      const value = Number(point.value);
      if (!Number.isFinite(value)) return pointMax;
      return value > pointMax ? value : pointMax;
    }, maxValue);
  }, 0);

  const axisMaxValue = Number(metricData?.yAxis?.max);
  const chartMax = Math.max(
    Number.isFinite(axisMaxValue) ? axisMaxValue : 0,
    maxVisibleValue,
    1,
  );
  const chartTicks = buildChartTicks(chartMax);

  const CHART_H = 230;
  const Y_COL_W = 72;
  const X_COL_W = 64;
  const plotW = Math.max(420, xValues.length * X_COL_W);
  const totalW = Y_COL_W + plotW;
  const scrollBoxWidth = containerPx > 0 ? containerPx : null;

  const pointX = (index) => {
    if (xValues.length <= 1) return plotW / 2;
    return (index / (xValues.length - 1)) * (plotW - 1);
  };

  const pointY = (value) => {
    const safeValue = Number(value);
    if (!Number.isFinite(safeValue)) return null;
    return CHART_H - Math.min(Math.max(safeValue / chartMax, 0), 1) * CHART_H;
  };

  const buildLinePath = (points, chartType) => {
    let pathData = "";
    let hasOpenSegment = false;
    for (let index = 0; index < points.length; index += 1) {
      const point = points[index];
      const y = pointY(point.value);
      if (y === null) { hasOpenSegment = false; continue; }
      const x = pointX(index);
      if (!hasOpenSegment) { pathData += `M ${x} ${y}`; hasOpenSegment = true; continue; }
      if (chartType === "step") {
        pathData += ` L ${x} ${pointY(points[index - 1].value)} L ${x} ${y}`;
      } else {
        pathData += ` L ${x} ${y}`;
      }
    }
    return pathData;
  };

  const TOOLTIP_H = 52 + visibleSeries.length * 22;

  const handlePlotMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const step = xValues.length > 1 ? (plotW - 1) / (xValues.length - 1) : plotW;
    const idx = Math.min(Math.max(Math.round(mouseX / step), 0), xValues.length - 1);
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    setHoveredXIndex(idx);
    setTooltip({
      xIndex: idx,
      x: Math.min(e.clientX + 16, vw - 240 - 8),
      y: Math.min(e.clientY - 10, vh - TOOLTIP_H - 8),
    });
  };

  const handlePlotMouseLeave = () => {
    setHoveredXIndex(null);
    setTooltip(null);
  };

  return (
    <>
      {/* ── Fixed crosshair tooltip ── */}
      {tooltip && hoveredXIndex !== null && (
        <div style={{
          position: "fixed",
          left: tooltip.x,
          top: tooltip.y,
          zIndex: 9999,
          background: "rgba(2,6,23,0.97)",
          border: "1px solid rgba(99,102,241,0.5)",
          borderRadius: "10px",
          padding: "11px 14px",
          minWidth: "220px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.65), 0 0 0 1px rgba(99,102,241,0.12)",
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}>
          <div style={{ fontSize: "10px", color: "#64748b", marginBottom: "8px", paddingBottom: "7px", borderBottom: "1px solid rgba(51,65,85,0.6)", fontVariantNumeric: "tabular-nums" }}>
            {formatTimeAxisLabel(xValues[hoveredXIndex])}
          </div>
          {visibleSeries.map((s) => {
            const point = s.points[hoveredXIndex];
            const val = point?.value;
            const hasVal = val !== null && val !== undefined && Number.isFinite(Number(val));
            return (
              <div key={`tip-ts-${s.id}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", fontSize: "10px", marginBottom: "4px" }}>
                <span style={{ display: "flex", alignItems: "center", gap: "5px", color: "#64748b" }}>
                  <span style={{ width: "8px", height: "2px", borderRadius: "1px", background: s.color, display: "inline-block", flexShrink: 0 }} />
                  <span style={{ maxWidth: "130px", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</span>
                </span>
                <span style={{ color: hasVal ? s.color : "#334155", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                  {hasVal ? `${formatTimeValue(val)}${valueUnit ? ` ${valueUnit}` : ""}` : "-"}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div
        ref={outerRef}
        style={{ width: "100%", overflow: "hidden", borderRadius: "12px", border: "1px solid rgba(51,65,85,0.7)", background: "rgba(15,23,42,0.86)" }}
      >
        <style>{`
          .time-series-scroll::-webkit-scrollbar { height: 7px; }
          .time-series-scroll::-webkit-scrollbar-track { background: rgba(15,23,42,0.9); border-radius: 4px; }
          .time-series-scroll::-webkit-scrollbar-thumb { background: #475569; border-radius: 4px; }
          .time-series-scroll::-webkit-scrollbar-thumb:hover { background: #64748b; }
          .ts-legend-area::-webkit-scrollbar { height: 3px; }
          .ts-legend-area::-webkit-scrollbar-track { background: transparent; }
          .ts-legend-area::-webkit-scrollbar-thumb { background: #334155; border-radius: 2px; }
        `}</style>

        {/* Header + scrollable toggle legend */}
        <div style={{ padding: "10px 14px 0 14px", borderBottom: "1px solid rgba(30,41,59,0.9)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "7px" }}>
            <span style={{ fontSize: "11px", fontWeight: 700, color: "#e2e8f0", letterSpacing: "0.02em" }}>
              {metricTitle}
            </span>
            {hiddenSeriesIds.size > 0 && (
              <button type="button" onClick={() => setHiddenSeriesIds(new Set())}
                style={{ fontSize: "9px", color: "#6366f1", background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.3)", borderRadius: "6px", padding: "2px 7px", cursor: "pointer", outline: "none" }}>
                전체 표시
              </button>
            )}
          </div>
          <div
            className="ts-legend-area"
            style={{ display: "flex", flexWrap: "wrap", gap: "4px", paddingBottom: "8px", maxHeight: "76px", overflowY: "auto", scrollbarWidth: "thin" }}
          >
            {series.map((s) => {
              const hidden = hiddenSeriesIds.has(s.id);
              return (
                <button
                  key={`ts-legend-pill-${s.id}`}
                  type="button"
                  onClick={() => {
                    setHiddenSeriesIds(prev => {
                      const next = new Set(prev);
                      if (next.has(s.id)) next.delete(s.id);
                      else next.add(s.id);
                      return next;
                    });
                  }}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "5px",
                    padding: "3px 9px 3px 7px", borderRadius: "99px",
                    border: `1px solid ${hidden ? "rgba(51,65,85,0.35)" : `${s.color}50`}`,
                    background: hidden ? "rgba(15,23,42,0.5)" : `${s.color}14`,
                    cursor: "pointer", opacity: hidden ? 0.45 : 1,
                    transition: "all 0.15s", outline: "none",
                  }}
                >
                  <span style={{ width: "10px", height: "2px", borderRadius: "1px", background: hidden ? "#334155" : s.color, flexShrink: 0 }} />
                  <span style={{ fontSize: "10px", color: hidden ? "#4b5563" : "#94a3b8", whiteSpace: "nowrap" }}>{s.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Scroll container */}
        <div
          className="time-series-scroll"
          style={{
            overflowX: "auto", overflowY: "hidden",
            scrollbarWidth: "thin", scrollbarColor: "#475569 rgba(15,23,42,0.9)",
            ...(scrollBoxWidth ? { width: `${scrollBoxWidth}px` } : { width: "100%" }),
          }}
        >
          <div style={{ width: `${totalW}px` }}>
            <div style={{ display: "flex", height: `${CHART_H + 44}px`, background: "rgba(2,6,23,0.5)" }}>
              {/* Y-axis */}
              <div style={{
                width: `${Y_COL_W}px`, flexShrink: 0, height: `${CHART_H}px`,
                display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "flex-end",
                padding: "2px 10px 2px 0", borderRight: "1px solid rgba(51,65,85,0.4)",
                boxSizing: "border-box", background: "rgba(2,6,23,0.6)",
              }}>
                {chartTicks.map((tick) => (
                  <span key={`time-series-tick-${tick.id}`} style={{ fontSize: "9px", color: "#64748b", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                    {formatTimeValue(tick.value)}
                  </span>
                ))}
              </div>

              {/* Plot area */}
              <div style={{ width: `${plotW}px`, position: "relative", height: `${CHART_H + 44}px` }}>
                {/* Grid lines */}
                {chartTicks.map((tick) => {
                  const y = pointY(tick.value);
                  return (
                    <div key={`time-series-grid-${tick.id}`} style={{
                      position: "absolute", left: 0, right: 0, top: `${y}px`,
                      borderTop: "1px solid rgba(30,41,59,0.7)", zIndex: 1, pointerEvents: "none",
                    }} />
                  );
                })}

                {/* Vertical crosshair */}
                {hoveredXIndex !== null && (
                  <div style={{
                    position: "absolute",
                    left: `${pointX(hoveredXIndex)}px`,
                    top: 0, width: "1px", height: `${CHART_H}px`,
                    background: "rgba(148,163,184,0.22)",
                    zIndex: 3, pointerEvents: "none",
                  }} />
                )}

                {/* SVG lines + hover dots */}
                <svg
                  width={plotW}
                  height={CHART_H}
                  style={{ position: "absolute", left: 0, top: 0, zIndex: 5, overflow: "visible" }}
                >
                  {visibleSeries.map((seriesItem) => {
                    const pathData = buildLinePath(seriesItem.points, metricData?.chartType);
                    if (!pathData) return null;
                    return (
                      <path
                        key={`time-series-path-${seriesItem.id}`}
                        d={pathData}
                        fill="none"
                        stroke={seriesItem.color}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        opacity="0.9"
                      />
                    );
                  })}

                  {/* Highlighted dots at hovered x-index */}
                  {hoveredXIndex !== null && visibleSeries.map((seriesItem) => {
                    const point = seriesItem.points[hoveredXIndex];
                    if (!point) return null;
                    const y = pointY(point.value);
                    if (y === null) return null;
                    return (
                      <circle
                        key={`ts-hover-dot-${seriesItem.id}`}
                        cx={pointX(hoveredXIndex)}
                        cy={y}
                        r={4.5}
                        fill={seriesItem.color}
                        stroke="rgba(2,6,23,0.95)"
                        strokeWidth="1.5"
                        pointerEvents="none"
                      />
                    );
                  })}

                  {/* Transparent overlay capturing mouse events */}
                  <rect
                    x={0} y={0}
                    width={plotW} height={CHART_H}
                    fill="transparent"
                    style={{ cursor: "crosshair" }}
                    onMouseMove={handlePlotMouseMove}
                    onMouseLeave={handlePlotMouseLeave}
                  />
                </svg>

                {/* X-axis labels */}
                <div style={{ position: "absolute", left: 0, right: 0, top: `${CHART_H}px`, height: "44px", display: "flex" }}>
                  {xValues.map((timeValue, timeIndex) => (
                    <div
                      key={`time-series-label-${timeValue || timeIndex}`}
                      style={{
                        width: `${plotW / Math.max(xValues.length, 1)}px`,
                        flexShrink: 0,
                        borderRight: "1px solid rgba(15,23,42,0.7)",
                        boxSizing: "border-box",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: hoveredXIndex === timeIndex ? "#cbd5e1" : "#94a3b8",
                        fontSize: "9px",
                        fontVariantNumeric: "tabular-nums",
                        transition: "color 0.1s",
                      }}
                    >
                      {formatTimeAxisLabel(timeValue)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function DarkSelectDropdown({
  value,
  onChange,
  options,
  placeholder = "선택...",
  disabled = false,
  accentColor = "#6366f1",
}) {
  const [open, setOpen] = useState(false);
  const [listPos, setListPos] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  const handleToggle = () => {
    if (disabled) return;
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setListPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      const inButton =
        buttonRef.current && buttonRef.current.contains(e.target);
      const inList = listRef.current && listRef.current.contains(e.target);
      if (!inButton && !inList) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selectedOption = options.find((o) => o.value === value) || null;

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={handleToggle}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          padding: "7px 11px",
          background: disabled ? "rgba(15,23,42,0.35)" : "rgba(10,14,28,0.9)",
          border: open
            ? `1px solid ${accentColor}70`
            : "1px solid rgba(51,65,85,0.6)",
          borderRadius: "8px",
          color: selectedOption ? "#e2e8f0" : "#4b5563",
          fontSize: "12px",
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "border-color 0.15s, box-shadow 0.15s",
          boxShadow: open ? `0 0 0 2px ${accentColor}18` : "none",
          outline: "none",
          opacity: disabled ? 0.4 : 1,
          textAlign: "left",
        }}
      >
        <span
          style={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            lineHeight: "1.4",
          }}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          style={{
            flexShrink: 0,
            transition: "transform 0.15s",
            transform: open ? "rotate(180deg)" : "none",
            color: accentColor,
            opacity: disabled ? 0.4 : 0.7,
          }}
        >
          <path
            d="M2.5 4.5L6 8L9.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && !disabled && (
        <div
          ref={listRef}
          style={{
            position: "fixed",
            top: listPos.top,
            left: listPos.left,
            width: listPos.width,
            zIndex: 9999,
            background: "rgba(8,12,24,0.98)",
            border: `1px solid ${accentColor}40`,
            borderRadius: "8px",
            boxShadow: `0 10px 28px rgba(0,0,0,0.55), 0 0 0 1px ${accentColor}10`,
            overflow: "hidden",
            maxHeight: "196px",
            overflowY: "auto",
          }}
        >
          {options.length === 0 ? (
            <div
              style={{
                padding: "10px 12px",
                fontSize: "11px",
                color: "#4b5563",
                textAlign: "center",
              }}
            >
              선택 가능한 항목 없음
            </div>
          ) : (
            options.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  style={{
                    width: "100%",
                    display: "block",
                    padding: "8px 12px",
                    fontSize: "12px",
                    textAlign: "left",
                    color: isSelected ? "#e2e8f0" : "#94a3b8",
                    background: isSelected ? `${accentColor}1a` : "transparent",
                    border: "none",
                    borderLeft: isSelected
                      ? `2px solid ${accentColor}`
                      : "2px solid transparent",
                    cursor: "pointer",
                    transition: "background 0.1s, color 0.1s",
                    outline: "none",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.background =
                        "rgba(255,255,255,0.04)";
                      e.currentTarget.style.color = "#e2e8f0";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = "#94a3b8";
                    }
                  }}
                >
                  {option.label}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function ProcLeadTimeMetricContent({ metricData }) {
  const orders = useMemo(() => {
    return Array.isArray(metricData?.orders) ? metricData.orders : [];
  }, [metricData?.orders]);

  const records = useMemo(() => {
    return Array.isArray(metricData?.records) ? metricData.records : [];
  }, [metricData?.records]);

  const [selectedOrderKey, setSelectedOrderKey] = useState("");
  const [selectedProc1Id, setSelectedProc1Id] = useState("");
  const [selectedProc2Id, setSelectedProc2Id] = useState("");

  useEffect(() => {
    if (!orders.length) {
      setSelectedOrderKey("");
      return;
    }

    if (orders.some((order) => order.orderKey === selectedOrderKey)) {
      return;
    }

    setSelectedOrderKey(orders[0].orderKey);
  }, [orders, selectedOrderKey]);

  const selectedOrder = useMemo(() => {
    return orders.find((order) => order.orderKey === selectedOrderKey) || null;
  }, [orders, selectedOrderKey]);

  const orderRecords = useMemo(() => {
    if (!selectedOrderKey) {
      return [];
    }
    return records.filter((record) => record.orderKey === selectedOrderKey);
  }, [records, selectedOrderKey]);

  const availableProc1Options = useMemo(() => {
    const filteredRecords = selectedProc2Id
      ? orderRecords.filter(
          (record) => String(record?.proc2?.id ?? "") === selectedProc2Id,
        )
      : orderRecords;
    return buildUniqueProcessOptions(filteredRecords, "proc1", "시작 공정");
  }, [orderRecords, selectedProc2Id]);

  const availableProc2Options = useMemo(() => {
    const filteredRecords = selectedProc1Id
      ? orderRecords.filter(
          (record) => String(record?.proc1?.id ?? "") === selectedProc1Id,
        )
      : orderRecords;
    return buildUniqueProcessOptions(filteredRecords, "proc2", "대상 공정");
  }, [orderRecords, selectedProc1Id]);

  useEffect(() => {
    if (!selectedOrderKey) {
      setSelectedProc1Id("");
      return;
    }

    if (
      selectedProc1Id &&
      availableProc1Options.some((proc) => proc.id === selectedProc1Id)
    ) {
      return;
    }

    if (availableProc1Options.length === 1) {
      setSelectedProc1Id(availableProc1Options[0].id);
      return;
    }

    if (selectedProc1Id) {
      setSelectedProc1Id("");
    }
  }, [availableProc1Options, selectedOrderKey, selectedProc1Id]);

  useEffect(() => {
    if (!selectedOrderKey) {
      setSelectedProc2Id("");
      return;
    }

    if (
      selectedProc2Id &&
      availableProc2Options.some((proc) => proc.id === selectedProc2Id)
    ) {
      return;
    }

    if (availableProc2Options.length === 1) {
      setSelectedProc2Id(availableProc2Options[0].id);
      return;
    }

    if (selectedProc2Id) {
      setSelectedProc2Id("");
    }
  }, [availableProc2Options, selectedOrderKey, selectedProc2Id]);

  const matchingRecords = useMemo(() => {
    return orderRecords.filter((record) => {
      const matchesProc1 =
        !selectedProc1Id || String(record?.proc1?.id ?? "") === selectedProc1Id;
      const matchesProc2 =
        !selectedProc2Id || String(record?.proc2?.id ?? "") === selectedProc2Id;
      return matchesProc1 && matchesProc2;
    });
  }, [orderRecords, selectedProc1Id, selectedProc2Id]);

  const selectedRecord = useMemo(() => {
    if (!matchingRecords.length) {
      return null;
    }

    if (selectedProc1Id && selectedProc2Id) {
      return matchingRecords[0] || null;
    }

    return matchingRecords.length === 1 ? matchingRecords[0] : null;
  }, [matchingRecords, selectedProc1Id, selectedProc2Id]);

  const emptyPlaceholderStyle = {
    padding: "20px 16px",
    textAlign: "center",
    color: "#334155",
    fontSize: "12px",
    border: "1px dashed rgba(51,65,85,0.4)",
    borderRadius: "8px",
    background: "rgba(10,14,28,0.4)",
  };

  if (!orders.length) {
    return (
      <div style={emptyPlaceholderStyle}>
        공정간 LeadTime 데이터가 없습니다.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {/* ── Row 1: 주문 선택 + 품목 정보 ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: "10px",
          alignItems: "stretch",
        }}
      >
        {/* 주문 선택 */}
        <div
          style={{
            background:
              "linear-gradient(135deg, rgba(15,23,42,0.92) 0%, rgba(20,30,52,0.88) 100%)",
            border: "1px solid rgba(51,65,85,0.55)",
            borderRadius: "12px",
            padding: "12px 14px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginBottom: "8px",
            }}
          >
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
              <rect
                x="1.5"
                y="1.5"
                width="9"
                height="9"
                rx="1.5"
                stroke="#6366f1"
                strokeWidth="1.3"
              />
              <path
                d="M3.5 4.5h5M3.5 6h5M3.5 7.5h3"
                stroke="#6366f1"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
            </svg>
            <span
              style={{
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.08em",
                color: "#64748b",
                textTransform: "uppercase",
              }}
            >
              주문 선택
            </span>
            <span
              style={{
                marginLeft: "auto",
                fontSize: "10px",
                color: "#475569",
                background: "rgba(71,85,105,0.15)",
                padding: "1px 7px",
                borderRadius: "9999px",
              }}
            >
              총 {orders.length}건
            </span>
          </div>
          <DarkSelectDropdown
            value={selectedOrderKey}
            onChange={(v) => {
              setSelectedOrderKey(v);
              setSelectedProc1Id("");
              setSelectedProc2Id("");
            }}
            options={orders.map((o) => ({
              value: o.orderKey,
              label: `${o.order.id} · ${o.order.name}`,
            }))}
            placeholder="주문을 선택하세요"
            accentColor="#6366f1"
          />
        </div>

        {/* 품목 정보 */}
        <div
          style={{
            background:
              "linear-gradient(135deg, rgba(15,23,42,0.92) 0%, rgba(20,30,52,0.88) 100%)",
            border: "1px solid rgba(51,65,85,0.45)",
            borderRadius: "12px",
            padding: "12px 14px",
            minWidth: "200px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginBottom: "8px",
            }}
          >
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
              <path
                d="M6 1.5L10.5 4v4L6 10.5 1.5 8V4L6 1.5z"
                stroke="#8b5cf6"
                strokeWidth="1.3"
                strokeLinejoin="round"
              />
              <path
                d="M6 4.5V7.5"
                stroke="#8b5cf6"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
            </svg>
            <span
              style={{
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.08em",
                color: "#64748b",
                textTransform: "uppercase",
              }}
            >
              품목 정보
            </span>
          </div>
          {selectedOrder ? (
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#c4b5fd",
                  marginBottom: "2px",
                }}
              >
                {selectedOrder.item.id}
              </div>
              <div style={{ fontSize: "11px", color: "#94a3b8" }}>
                {selectedOrder.item.name}
              </div>
            </div>
          ) : (
            <div
              style={{
                fontSize: "11px",
                color: "#374151",
                fontStyle: "italic",
                flex: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              주문 선택 후 표시
            </div>
          )}
        </div>
      </div>

      {/* ── Row 2: 공정 선택 ── */}
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}
      >
        {/* 시작 공정 */}
        <div
          style={{
            background:
              "linear-gradient(135deg, rgba(6,182,212,0.05) 0%, rgba(10,14,28,0.92) 100%)",
            border: "1px solid rgba(6,182,212,0.22)",
            borderRadius: "12px",
            padding: "12px 14px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "3px",
              background: "linear-gradient(180deg, #22d3ee 0%, #0891b2 100%)",
              borderRadius: "12px 0 0 12px",
            }}
          />
          <div style={{ paddingLeft: "8px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "8px",
              }}
            >
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  background: "#06b6d4",
                  flexShrink: 0,
                  display: "block",
                  boxShadow: "0 0 5px #06b6d480",
                }}
              />
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  color: "#67e8f9",
                  textTransform: "uppercase",
                }}
              >
                시작 공정
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "10px",
                  color: "#164e63",
                  background: "rgba(6,182,212,0.1)",
                  padding: "1px 7px",
                  borderRadius: "9999px",
                }}
              >
                {availableProc1Options.length}건
              </span>
            </div>
            <DarkSelectDropdown
              value={selectedProc1Id}
              onChange={setSelectedProc1Id}
              options={availableProc1Options.map((p) => ({
                value: p.id,
                label: `${p.id} · ${p.name}`,
              }))}
              placeholder="시작 공정 선택"
              disabled={!selectedOrderKey || availableProc1Options.length === 0}
              accentColor="#06b6d4"
            />
          </div>
        </div>

        {/* 대상 공정 */}
        <div
          style={{
            background:
              "linear-gradient(135deg, rgba(139,92,246,0.05) 0%, rgba(10,14,28,0.92) 100%)",
            border: "1px solid rgba(139,92,246,0.22)",
            borderRadius: "12px",
            padding: "12px 14px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "3px",
              background: "linear-gradient(180deg, #a78bfa 0%, #7c3aed 100%)",
              borderRadius: "12px 0 0 12px",
            }}
          />
          <div style={{ paddingLeft: "8px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "8px",
              }}
            >
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  background: "#8b5cf6",
                  flexShrink: 0,
                  display: "block",
                  boxShadow: "0 0 5px #8b5cf680",
                }}
              />
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  color: "#c4b5fd",
                  textTransform: "uppercase",
                }}
              >
                대상 공정
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "10px",
                  color: "#2e1065",
                  background: "rgba(139,92,246,0.1)",
                  padding: "1px 7px",
                  borderRadius: "9999px",
                }}
              >
                {availableProc2Options.length}건
              </span>
            </div>
            <DarkSelectDropdown
              value={selectedProc2Id}
              onChange={setSelectedProc2Id}
              options={availableProc2Options.map((p) => ({
                value: p.id,
                label: `${p.id} · ${p.name}`,
              }))}
              placeholder="대상 공정 선택"
              disabled={!selectedOrderKey || availableProc2Options.length === 0}
              accentColor="#8b5cf6"
            />
          </div>
        </div>
      </div>

      {/* ── Row 3: LeadTime 지표 위젯 ── */}
      <div
        style={{
          background:
            "linear-gradient(135deg, rgba(13,18,36,0.95) 0%, rgba(17,24,42,0.95) 100%)",
          border: "1px solid rgba(51,65,85,0.45)",
          borderRadius: "12px",
          padding: "14px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "10px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <circle
                cx="6"
                cy="6"
                r="4.5"
                stroke="#f59e0b"
                strokeWidth="1.3"
              />
              <path
                d="M6 3.5v2.8l1.8 1.8"
                stroke="#f59e0b"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span
              style={{
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.08em",
                color: "#64748b",
                textTransform: "uppercase",
              }}
            >
              LeadTime 지표
            </span>
          </div>
          <span style={{ fontSize: "10px", color: "#334155" }}>
            일치 레코드 {matchingRecords.length}건
          </span>
        </div>

        {!selectedOrderKey && (
          <div style={emptyPlaceholderStyle}>
            주문을 선택하면 LeadTime 지표를 확인할 수 있습니다
          </div>
        )}

        {selectedOrderKey && orderRecords.length === 0 && (
          <div style={emptyPlaceholderStyle}>
            선택한 주문에 대한 공정간 LeadTime 데이터가 없습니다
          </div>
        )}

        {selectedOrderKey && orderRecords.length > 0 && !selectedRecord && (
          <div style={emptyPlaceholderStyle}>
            시작 공정과 대상 공정을 선택하면 LeadTime 값을 표시합니다
          </div>
        )}

        {selectedRecord && (
          <>
            {/* 공정 흐름 경로 표시 */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 12px",
                marginBottom: "12px",
                background: "rgba(99,102,241,0.07)",
                border: "1px solid rgba(99,102,241,0.18)",
                borderRadius: "8px",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <span
                  style={{
                    width: "5px",
                    height: "5px",
                    borderRadius: "50%",
                    background: "#22d3ee",
                    display: "inline-block",
                    flexShrink: 0,
                    boxShadow: "0 0 4px #22d3ee",
                  }}
                />
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#67e8f9",
                  }}
                >
                  {selectedRecord.proc1.name}
                </span>
                <span style={{ fontSize: "10px", color: "#475569" }}>
                  ({selectedRecord.proc1.id})
                </span>
              </div>
              <svg
                width="22"
                height="10"
                viewBox="0 0 22 10"
                fill="none"
                style={{ flexShrink: 0 }}
              >
                <path
                  d="M1 5h16M14 1.5l4.5 3.5L14 8.5"
                  stroke="#6366f1"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <span
                  style={{
                    width: "5px",
                    height: "5px",
                    borderRadius: "50%",
                    background: "#a78bfa",
                    display: "inline-block",
                    flexShrink: 0,
                    boxShadow: "0 0 4px #a78bfa",
                  }}
                />
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#c4b5fd",
                  }}
                >
                  {selectedRecord.proc2.name}
                </span>
                <span style={{ fontSize: "10px", color: "#475569" }}>
                  ({selectedRecord.proc2.id})
                </span>
              </div>
            </div>

            {/* MIN / MAX / AVG 위젯 카드 */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "8px",
              }}
            >
              {/* MIN */}
              <div
                style={{
                  background:
                    "linear-gradient(160deg, rgba(16,185,129,0.07) 0%, rgba(10,14,28,0.95) 100%)",
                  border: "1px solid rgba(16,185,129,0.22)",
                  borderRadius: "10px",
                  padding: "14px 12px",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "2px",
                    background:
                      "linear-gradient(90deg, #10b981 0%, transparent 80%)",
                  }}
                />
                <div
                  style={{
                    fontSize: "9px",
                    fontWeight: 800,
                    letterSpacing: "0.12em",
                    color: "#34d399",
                    textTransform: "uppercase",
                    marginBottom: "10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                    <path
                      d="M4 7V1M1.5 4.5L4 7l2.5-2.5"
                      stroke="#34d399"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  MIN
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: 900,
                    color: "#ecfdf5",
                    lineHeight: 1,
                    letterSpacing: "-0.02em",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatTimeValue(selectedRecord.leadtime?.min)}
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    color: "#6ee7b7",
                    marginTop: "4px",
                    fontWeight: 500,
                  }}
                >
                  sec
                </div>
              </div>

              {/* MAX */}
              <div
                style={{
                  background:
                    "linear-gradient(160deg, rgba(245,158,11,0.07) 0%, rgba(10,14,28,0.95) 100%)",
                  border: "1px solid rgba(245,158,11,0.22)",
                  borderRadius: "10px",
                  padding: "14px 12px",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "2px",
                    background:
                      "linear-gradient(90deg, #f59e0b 0%, transparent 80%)",
                  }}
                />
                <div
                  style={{
                    fontSize: "9px",
                    fontWeight: 800,
                    letterSpacing: "0.12em",
                    color: "#fbbf24",
                    textTransform: "uppercase",
                    marginBottom: "10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                    <path
                      d="M4 1v6M1.5 3.5L4 1l2.5 2.5"
                      stroke="#fbbf24"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  MAX
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: 900,
                    color: "#fffbeb",
                    lineHeight: 1,
                    letterSpacing: "-0.02em",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatTimeValue(selectedRecord.leadtime?.max)}
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    color: "#fcd34d",
                    marginTop: "4px",
                    fontWeight: 500,
                  }}
                >
                  sec
                </div>
              </div>

              {/* AVG */}
              <div
                style={{
                  background:
                    "linear-gradient(160deg, rgba(56,189,248,0.07) 0%, rgba(10,14,28,0.95) 100%)",
                  border: "1px solid rgba(56,189,248,0.22)",
                  borderRadius: "10px",
                  padding: "14px 12px",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "2px",
                    background:
                      "linear-gradient(90deg, #38bdf8 0%, transparent 80%)",
                  }}
                />
                <div
                  style={{
                    fontSize: "9px",
                    fontWeight: 800,
                    letterSpacing: "0.12em",
                    color: "#7dd3fc",
                    textTransform: "uppercase",
                    marginBottom: "10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                    <path
                      d="M1.5 4h5"
                      stroke="#7dd3fc"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                    <circle cx="4" cy="4" r="1.2" fill="#7dd3fc" />
                  </svg>
                  AVG
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: 900,
                    color: "#f0f9ff",
                    lineHeight: 1,
                    letterSpacing: "-0.02em",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatTimeValue(selectedRecord.leadtime?.avg)}
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    color: "#bae6fd",
                    marginTop: "4px",
                    fontWeight: 500,
                  }}
                >
                  sec
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LineBalancingMetricContent({ metricData }) {
  const outerRef = useRef(null);
  const [containerPx, setContainerPx] = useState(0);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => {
      setContainerPx(Math.floor(entry.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const processes = Array.isArray(metricData?.processes)
    ? metricData.processes
    : [];

  if (processes.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/70 px-4 py-8 text-center text-xs text-slate-500">
        Line Balancing 데이터가 없습니다.
      </div>
    );
  }

  const pitchBaseline = Number(metricData?.pitchBaseline);
  const safePitchBaseline = Number.isFinite(pitchBaseline) ? pitchBaseline : 0;

  const yAxisMax = Number(metricData?.yAxisMax);
  const safeYAxisMax = Number.isFinite(yAxisMax) && yAxisMax > 0 ? yAxisMax : 0;
  const chartMax =
    safeYAxisMax > 0
      ? safeYAxisMax
      : Math.max(
          ...processes.map((p) =>
            Math.max(
              Number.isFinite(Number(p.lbValue)) ? Number(p.lbValue) : 0,
              Number.isFinite(Number(p.resultTime)) ? Number(p.resultTime) : 0,
            ),
          ),
          1,
        );

  const CHART_H = 240;
  const Y_COL_W = 72;
  const PROC_COL_W = 96;
  const totalW = Y_COL_W + processes.length * PROC_COL_W;

  const chartTicks = buildChartTicks(chartMax);
  const baselinePct = Math.min((safePitchBaseline / chartMax) * 100, 100);

  const TABLE_ROWS = [
    { label: "공정명", getValue: (p) => p.procName },
    {
      label: "공정시간",
      getValue: (p) => `${formatTimeValue(p.procTime)} sec`,
    },
    {
      label: "장비 수",
      getValue: (p) => `${formatIntegerValue(p.mchQuantity)} 대`,
    },
    {
      label: "실적Takt",
      getValue: (p) => `${formatTimeValue(p.resultTime)} sec`,
    },
  ];

  // scrollBoxWidth: ResizeObserver로 측정한 실제 픽셀 너비 → overflow-x:auto가 확정값을 가지게 함
  const scrollBoxWidth = containerPx > 0 ? containerPx : null;

  const handleBarMouseMove = (process, safeLb, safeResult, e) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const TW = 170;
    const TH = 155;
    const rawX = e.clientX + 14;
    const rawY = e.clientY - 10;
    setTooltip({
      process,
      safeLb,
      safeResult,
      x: Math.min(rawX, vw - TW - 8),
      y: Math.min(rawY, vh - TH - 8),
    });
  };

  const handleMouseLeave = () => setTooltip(null);

  return (
    <>
      {/* ── Fixed-position Tooltip — position:fixed로 모든 overflow 제약 탈출 ── */}
      {tooltip && (
        <div
          style={{
            position: "fixed",
            left: tooltip.x,
            top: tooltip.y,
            zIndex: 9999,
            background: "rgba(2,6,23,0.97)",
            border: "1px solid rgba(99,102,241,0.55)",
            borderRadius: "10px",
            padding: "11px 14px",
            minWidth: "160px",
            boxShadow:
              "0 12px 40px rgba(0,0,0,0.65), 0 0 0 1px rgba(99,102,241,0.15)",
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#f1f5f9",
              marginBottom: "9px",
              paddingBottom: "7px",
              borderBottom: "1px solid rgba(51,65,85,0.6)",
            }}
          >
            {tooltip.process.procName}
          </div>
          {[
            {
              label: "LB 값",
              value: `${formatTimeValue(tooltip.safeLb)} sec`,
              color: "#5eead4",
            },
            {
              label: "실적Takt",
              value: `${formatTimeValue(tooltip.safeResult)} sec`,
              color: "#818cf8",
            },
            {
              label: "공정시간",
              value: `${formatTimeValue(tooltip.process.procTime)} sec`,
              color: "#94a3b8",
            },
            {
              label: "장비 수",
              value: `${formatIntegerValue(tooltip.process.mchQuantity)} 대`,
              color: "#94a3b8",
            },
          ].map(({ label, value, color }) => (
            <div
              key={label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "18px",
                fontSize: "10px",
                marginBottom: "4px",
                alignItems: "center",
              }}
            >
              <span style={{ color: "#64748b" }}>{label}</span>
              <span
                style={{
                  color,
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {value}
              </span>
            </div>
          ))}
          <div
            style={{
              marginTop: "7px",
              paddingTop: "7px",
              borderTop: "1px solid rgba(51,65,85,0.5)",
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              fontSize: "10px",
            }}
          >
            <span style={{ color: "#64748b" }}>Pitch 대비</span>
            <span
              style={{
                color:
                  tooltip.safeLb <= safePitchBaseline ? "#34d399" : "#f87171",
                fontWeight: 700,
              }}
            >
              {tooltip.safeLb <= safePitchBaseline
                ? "▼ 기준 이하"
                : "▲ 기준 초과"}
            </span>
          </div>
        </div>
      )}

      {/* ── Chart card ── */}
      <div
        ref={outerRef}
        style={{
          width: "100%",
          overflow: "hidden",
          borderRadius: "12px",
          border: "1px solid rgba(51,65,85,0.7)",
          background: "rgba(15,23,42,0.85)",
        }}
      >
        <style>{`
          .lb-chart-scroll::-webkit-scrollbar { height: 7px; }
          .lb-chart-scroll::-webkit-scrollbar-track { background: rgba(15,23,42,0.9); border-radius: 4px; }
          .lb-chart-scroll::-webkit-scrollbar-thumb { background: #475569; border-radius: 4px; }
          .lb-chart-scroll::-webkit-scrollbar-thumb:hover { background: #64748b; }
        `}</style>

        {/* Header / Legend */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 14px",
            borderBottom: "1px solid rgba(30,41,59,0.9)",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <span
            style={{
              fontSize: "11px",
              fontWeight: 600,
              color: "#e2e8f0",
              letterSpacing: "0.02em",
            }}
          >
            공정시간 (sec)
          </span>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "10px",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: "14px",
                  height: "10px",
                  borderRadius: "3px",
                  background: "linear-gradient(to top, #0d9488, #5eead4)",
                }}
              />
              <span style={{ color: "#94a3b8" }}>Line Balancing 값</span>
            </span>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "10px",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: "14px",
                  height: "10px",
                  borderRadius: "2px",
                  background: "rgba(99,102,241,0.35)",
                  border: "1px solid rgba(99,102,241,0.4)",
                }}
              />
              <span style={{ color: "#94a3b8" }}>실적Takt</span>
            </span>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "10px",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: "18px",
                  height: "2px",
                  background: "rgba(251,191,36,0.85)",
                  borderRadius: "1px",
                }}
              />
              <span style={{ color: "#fbbf24" }}>
                Pitch {formatTimeValue(safePitchBaseline)} sec
              </span>
            </span>
          </div>
        </div>

        {/* Scroll container — ResizeObserver로 측정한 픽셀 너비를 명시 설정 */}
        <div
          className="lb-chart-scroll"
          style={{
            overflowX: "auto",
            overflowY: "visible",
            scrollbarWidth: "thin",
            scrollbarColor: "#475569 rgba(15,23,42,0.9)",
            // 측정된 픽셀값을 쓰면 CSS 캐스케이드와 무관하게 overflow가 확정됨
            ...(scrollBoxWidth
              ? { width: `${scrollBoxWidth}px` }
              : { width: "100%" }),
          }}
        >
          <div style={{ width: `${totalW}px` }}>
            {/* ── Chart area ── */}
            <div
              style={{
                display: "flex",
                height: `${CHART_H}px`,
                background: "rgba(2,6,23,0.5)",
              }}
            >
              {/* Y-axis */}
              <div
                style={{
                  width: `${Y_COL_W}px`,
                  flexShrink: 0,
                  height: `${CHART_H}px`,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  padding: "2px 10px 2px 0",
                  borderRight: "1px solid rgba(51,65,85,0.4)",
                  boxSizing: "border-box",
                  background: "rgba(2,6,23,0.6)",
                }}
              >
                {chartTicks.map((tick) => (
                  <span
                    key={tick.id}
                    style={{
                      fontSize: "9px",
                      color: "#64748b",
                      lineHeight: 1,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatTimeValue(tick.value)}
                  </span>
                ))}
              </div>

              {/* Bar columns */}
              {processes.map((process) => {
                const safeLb = Math.max(
                  0,
                  Number.isFinite(Number(process.lbValue))
                    ? Number(process.lbValue)
                    : 0,
                );
                const safeResult = Math.max(
                  0,
                  Number.isFinite(Number(process.resultTime))
                    ? Number(process.resultTime)
                    : 0,
                );
                const resultH = Math.round(
                  Math.min(safeResult / chartMax, 1) * CHART_H,
                );
                const lbH = Math.round(
                  Math.min(safeLb / chartMax, 1) * CHART_H,
                );
                const isHovered = tooltip?.process?.procId === process.procId;

                return (
                  <div
                    key={`chart-col-${process.procId}`}
                    onMouseMove={(e) =>
                      handleBarMouseMove(process, safeLb, safeResult, e)
                    }
                    onMouseLeave={handleMouseLeave}
                    style={{
                      width: `${PROC_COL_W}px`,
                      flexShrink: 0,
                      height: `${CHART_H}px`,
                      position: "relative",
                      borderRight: "1px solid rgba(15,23,42,0.7)",
                      boxSizing: "border-box",
                      background: isHovered
                        ? "rgba(99,102,241,0.06)"
                        : "transparent",
                      transition: "background 0.15s ease",
                      cursor: "crosshair",
                    }}
                  >
                    {/* Grid lines */}
                    {chartTicks.map((tick) => {
                      const pct = Math.min((tick.value / chartMax) * 100, 100);
                      return (
                        <div
                          key={`gl-${tick.id}`}
                          style={{
                            position: "absolute",
                            left: 0,
                            right: "-1px",
                            bottom: `${pct}%`,
                            borderTop: "1px solid rgba(30,41,59,0.7)",
                            pointerEvents: "none",
                          }}
                        />
                      );
                    })}

                    {/* Pitch baseline */}
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        right: "-1px",
                        bottom: `${baselinePct}%`,
                        borderTop: "2px solid rgba(251,191,36,0.75)",
                        zIndex: 10,
                        pointerEvents: "none",
                      }}
                    />

                    {/* result_time bar */}
                    {resultH > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: 0,
                          left: "13%",
                          right: "13%",
                          height: `${resultH}px`,
                          background: isHovered
                            ? "rgba(99,102,241,0.38)"
                            : "rgba(99,102,241,0.28)",
                          border: "1px solid rgba(99,102,241,0.35)",
                          borderBottom: "none",
                          borderRadius: "4px 4px 0 0",
                          transition: "background 0.15s ease",
                        }}
                      />
                    )}

                    {/* lb_value bar */}
                    {lbH > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: 0,
                          left: "13%",
                          right: "13%",
                          height: `${lbH}px`,
                          background: isHovered
                            ? "linear-gradient(to top, #0f766e, #99f6e4)"
                            : "linear-gradient(to top, #0d9488, #5eead4)",
                          borderRadius: "4px 4px 0 0",
                          zIndex: 5,
                          boxShadow: isHovered
                            ? "0 0 14px rgba(20,184,166,0.45)"
                            : "none",
                          transition:
                            "background 0.15s ease, box-shadow 0.15s ease",
                        }}
                      />
                    )}

                    {/* lb_value label */}
                    <div
                      style={{
                        position: "absolute",
                        zIndex: 20,
                        left: 0,
                        right: 0,
                        bottom:
                          lbH > 18
                            ? `${lbH - 14}px`
                            : `${Math.max(lbH + 3, 3)}px`,
                        textAlign: "center",
                        fontSize: "10px",
                        fontWeight: 700,
                        color: lbH > 18 ? "#f0fdfa" : "#64748b",
                        pointerEvents: "none",
                        textShadow:
                          lbH > 18 ? "0 1px 2px rgba(0,0,0,0.7)" : "none",
                      }}
                    >
                      {formatTimeValue(safeLb)}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Table rows ── */}
            {TABLE_ROWS.map((row, rowIdx) => (
              <div
                key={row.label}
                style={{
                  display: "flex",
                  borderTop: "1px solid rgba(30,41,59,0.9)",
                  background:
                    rowIdx % 2 === 0
                      ? "rgba(2,6,23,0.4)"
                      : "rgba(15,23,42,0.3)",
                }}
              >
                <div
                  style={{
                    width: `${Y_COL_W}px`,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    padding: "7px 10px",
                    fontSize: "10px",
                    fontWeight: 600,
                    color: "#64748b",
                    background: "rgba(2,6,23,0.55)",
                    borderRight: "1px solid rgba(51,65,85,0.4)",
                    boxSizing: "border-box",
                    letterSpacing: "0.02em",
                  }}
                >
                  {row.label}
                </div>
                {processes.map((process) => {
                  const safeLb = Math.max(
                    0,
                    Number.isFinite(Number(process.lbValue))
                      ? Number(process.lbValue)
                      : 0,
                  );
                  const safeResult = Math.max(
                    0,
                    Number.isFinite(Number(process.resultTime))
                      ? Number(process.resultTime)
                      : 0,
                  );
                  const displayValue = row.getValue(process);
                  const isHovered = tooltip?.process?.procId === process.procId;
                  return (
                    <div
                      key={`tr-${row.label}-${process.procId}`}
                      onMouseMove={(e) =>
                        handleBarMouseMove(process, safeLb, safeResult, e)
                      }
                      onMouseLeave={handleMouseLeave}
                      title={String(displayValue)}
                      style={{
                        width: `${PROC_COL_W}px`,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "7px 4px",
                        fontSize: "10px",
                        color: isHovered ? "#e2e8f0" : "#94a3b8",
                        borderRight: "1px solid rgba(15,23,42,0.7)",
                        overflow: "hidden",
                        boxSizing: "border-box",
                        background: isHovered
                          ? "rgba(99,102,241,0.08)"
                          : "transparent",
                        transition: "background 0.15s ease, color 0.15s ease",
                        fontVariantNumeric: "tabular-nums",
                        cursor: "default",
                      }}
                    >
                      <span
                        style={{
                          display: "block",
                          width: "100%",
                          textAlign: "center",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {displayValue}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function ResultPanelCard({
  panelTitle,
  resultItem,
  compareTemplate,
  orderText,
  emptyHint,
  utilizationMchMetric,
  utilizationMchLoading,
  utilizationMchError,
  utilizationLogisticsMetric,
  utilizationLogisticsLoading,
  utilizationLogisticsError,
  utilizationWorkerMetric,
  utilizationWorkerLoading,
  utilizationWorkerError,
  mchBufferMetric,
  mchBufferLoading,
  mchBufferError,
  taktTimeMetric,
  taktTimeLoading,
  taktTimeError,
  procLeadTimeMetric,
  procLeadTimeLoading,
  procLeadTimeError,
  lineBalancingMetric,
  lineBalancingLoading,
  lineBalancingError,
  uphMetric,
  uphLoading,
  uphError,
  logisticsCongestionMetric,
  logisticsCongestionLoading,
  logisticsCongestionError,
}) {
  const selectedResult = resultItem || null;

  return (
    <div
      className="flex min-h-[360px] min-w-0 flex-col rounded-xl border border-slate-800 bg-slate-900/45"
      style={{ maxHeight: "720px", overflow: "hidden" }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 px-4 py-3">
        <div>
          <h4 className="text-sm font-bold text-slate-200">
            {orderText ? `${orderText}. ${panelTitle}` : panelTitle}
          </h4>
          <p className="mt-0.5 text-[11px] text-slate-500">
            {selectedResult
              ? selectedResult.tableName
              : "결과 항목을 선택해주세요."}
          </p>
        </div>
      </div>

      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto p-4 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1">
        {!selectedResult && (
          <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/60 px-4 py-8 text-center text-xs text-slate-500">
            {emptyHint ||
              "표시할 결과 항목을 선택하면 이 영역에 지표 결과가 표시됩니다."}
          </div>
        )}

        {selectedResult && (
          <>
            {selectedResult.tableName === "sim_res_proc_leadtime" ? (
              procLeadTimeLoading ? (
                <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-8 text-center text-xs text-slate-400">
                  공정간 LeadTime 데이터를 불러오는 중입니다...
                </div>
              ) : procLeadTimeError ? (
                <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-8 text-center text-xs text-rose-200">
                  {procLeadTimeError}
                </div>
              ) : (
                <ProcLeadTimeMetricContent metricData={procLeadTimeMetric} />
              )
            ) : selectedResult.tableName === "sim_res_takttime" ? (
              taktTimeLoading ? (
                <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-8 text-center text-xs text-slate-400">
                  Takt Time 데이터를 불러오는 중입니다...
                </div>
              ) : taktTimeError ? (
                <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-8 text-center text-xs text-rose-200">
                  {taktTimeError}
                </div>
              ) : (
                <TaktTimeMetricContent metricData={taktTimeMetric} />
              )
            ) : selectedResult.tableName === "sim_res_utilization_mch" ? (
              utilizationMchLoading ? (
                <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-8 text-center text-xs text-slate-400">
                  장비 가동실적 데이터를 불러오는 중입니다...
                </div>
              ) : utilizationMchError ? (
                <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-8 text-center text-xs text-rose-200">
                  {utilizationMchError}
                </div>
              ) : (
                <UtilizationMetricContent
                  metricData={utilizationMchMetric}
                  metricTitle="장비별 가동 실적"
                  isPercentMetric
                />
              )
            ) : selectedResult.tableName === "sim_res_utilization_logistics" ? (
              utilizationLogisticsLoading ? (
                <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-8 text-center text-xs text-slate-400">
                  물류 가동실적 데이터를 불러오는 중입니다...
                </div>
              ) : utilizationLogisticsError ? (
                <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-8 text-center text-xs text-rose-200">
                  {utilizationLogisticsError}
                </div>
              ) : (
                <UtilizationMetricContent
                  metricData={utilizationLogisticsMetric}
                  metricTitle="물류 가동 실적"
                  isPercentMetric
                />
              )
            ) : selectedResult.tableName === "sim_res_utilization_worker" ? (
              utilizationWorkerLoading ? (
                <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-8 text-center text-xs text-slate-400">
                  작업자 가동실적 데이터를 불러오는 중입니다...
                </div>
              ) : utilizationWorkerError ? (
                <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-8 text-center text-xs text-rose-200">
                  {utilizationWorkerError}
                </div>
              ) : (
                <UtilizationMetricContent
                  metricData={utilizationWorkerMetric}
                  metricTitle="작업자 가동 실적"
                  isPercentMetric
                />
              )
            ) : selectedResult.tableName === "sim_res_mch_buffer" ? (
              mchBufferLoading ? (
                <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-8 text-center text-xs text-slate-400">
                  장비 Buffer 데이터를 불러오는 중입니다...
                </div>
              ) : mchBufferError ? (
                <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-8 text-center text-xs text-rose-200">
                  {mchBufferError}
                </div>
              ) : (
                <UtilizationMetricContent
                  metricData={mchBufferMetric}
                  metricTitle="장비 Buffer 상태"
                  isPercentMetric={false}
                />
              )
            ) : selectedResult.tableName === "sim_res_linebalancing" ? (
              lineBalancingLoading ? (
                <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-8 text-center text-xs text-slate-400">
                  Line Balancing 데이터를 불러오는 중입니다...
                </div>
              ) : lineBalancingError ? (
                <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-8 text-center text-xs text-rose-200">
                  {lineBalancingError}
                </div>
              ) : (
                <LineBalancingMetricContent metricData={lineBalancingMetric} />
              )
            ) : selectedResult.tableName === "sim_res_uph" ? (
              uphLoading ? (
                <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-8 text-center text-xs text-slate-400">
                  UPH 데이터를 불러오는 중입니다...
                </div>
              ) : uphError ? (
                <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-8 text-center text-xs text-rose-200">
                  {uphError}
                </div>
              ) : (
                <TimeSeriesMetricContent
                  metricData={uphMetric}
                  metricTitle="UPH 추이"
                  valueLabel="UPH"
                  valueUnit="ea/h"
                  emptyMessage="UPH 데이터가 없습니다."
                />
              )
            ) : selectedResult.tableName === "sim_res_logistics_congestion" ? (
              logisticsCongestionLoading ? (
                <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-8 text-center text-xs text-slate-400">
                  물류 혼잡 데이터를 불러오는 중입니다...
                </div>
              ) : logisticsCongestionError ? (
                <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-8 text-center text-xs text-rose-200">
                  {logisticsCongestionError}
                </div>
              ) : (
                <TimeSeriesMetricContent
                  metricData={logisticsCongestionMetric}
                  metricTitle="물류 혼잡도 추이"
                  valueLabel="물류 대수"
                  valueUnit="대"
                  emptyMessage="물류 혼잡 데이터가 없습니다."
                />
              )
            ) : selectedResult.renderType === "table" ? (
              <ResultTablePlaceholder
                columns={selectedResult.keyColumns || []}
              />
            ) : (
              <ResultChartPlaceholder
                title={`${selectedResult.label} 그래프`}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function SimulationResultPopup({
  open,
  onClose,
  groupCode,
  projectCode,
}) {
  const rootUrl = useMemo(() => API_URL.replace("/backend", ""), []);
  const resultItems = useMemo(() => buildResultItems(), []);
  const resultItemById = useMemo(
    () => new Map(resultItems.map((item) => [item.id, item])),
    [resultItems],
  );

  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedResultIds, setSelectedResultIds] = useState([]);
  const [isCompareMode, setIsCompareMode] = useState(false);

  const [simulationOptions, setSimulationOptions] = useState([]);
  const [simulationLoading, setSimulationLoading] = useState(false);
  const [simulationError, setSimulationError] = useState("");
  const [leftSimulationOptionId, setLeftSimulationOptionId] = useState(
    DEFAULT_LEFT_SIMULATION_OPTION_ID,
  );
  const [rightSimulationOptionId, setRightSimulationOptionId] = useState(
    DEFAULT_RIGHT_SIMULATION_OPTION_ID,
  );

  const [procLeadTimeMetric, setProcLeadTimeMetric] = useState(null);
  const [procLeadTimeLoading, setProcLeadTimeLoading] = useState(false);
  const [procLeadTimeError, setProcLeadTimeError] = useState("");

  const [taktTimeMetric, setTaktTimeMetric] = useState(null);
  const [taktTimeLoading, setTaktTimeLoading] = useState(false);
  const [taktTimeError, setTaktTimeError] = useState("");

  const [utilizationMchMetric, setUtilizationMchMetric] = useState(null);
  const [utilizationMchLoading, setUtilizationMchLoading] = useState(false);
  const [utilizationMchError, setUtilizationMchError] = useState("");

  const [utilizationLogisticsMetric, setUtilizationLogisticsMetric] =
    useState(null);
  const [utilizationLogisticsLoading, setUtilizationLogisticsLoading] =
    useState(false);
  const [utilizationLogisticsError, setUtilizationLogisticsError] =
    useState("");

  const [utilizationWorkerMetric, setUtilizationWorkerMetric] = useState(null);
  const [utilizationWorkerLoading, setUtilizationWorkerLoading] =
    useState(false);
  const [utilizationWorkerError, setUtilizationWorkerError] = useState("");

  const [mchBufferMetric, setMchBufferMetric] = useState(null);
  const [mchBufferLoading, setMchBufferLoading] = useState(false);
  const [mchBufferError, setMchBufferError] = useState("");

  const [lineBalancingMetric, setLineBalancingMetric] = useState(null);
  const [lineBalancingLoading, setLineBalancingLoading] = useState(false);
  const [lineBalancingError, setLineBalancingError] = useState("");

  const [uphMetric, setUphMetric] = useState(null);
  const [uphLoading, setUphLoading] = useState(false);
  const [uphError, setUphError] = useState("");

  const [logisticsCongestionMetric, setLogisticsCongestionMetric] =
    useState(null);
  const [logisticsCongestionLoading, setLogisticsCongestionLoading] =
    useState(false);
  const [logisticsCongestionError, setLogisticsCongestionError] = useState("");

  const selectedResultItems = useMemo(
    () => selectedResultIds.map((id) => resultItemById.get(id)).filter(Boolean),
    [resultItemById, selectedResultIds],
  );

  const selectedOrderMap = useMemo(
    () => new Map(selectedResultIds.map((id, index) => [id, index + 1])),
    [selectedResultIds],
  );

  const simulationOptionById = useMemo(
    () => new Map(simulationOptions.map((option) => [option.id, option])),
    [simulationOptions],
  );

  const leftSimulationOption =
    simulationOptionById.get(leftSimulationOptionId) || null;
  const rightSimulationOption =
    simulationOptionById.get(rightSimulationOptionId) || null;

  const filteredGroups = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();

    return RESULT_GROUPS.map((group) => {
      const items = resultItems.filter((item) => {
        if (item.groupId !== group.id) {
          return false;
        }

        if (!keyword) {
          return true;
        }

        return `${item.label} ${item.tableName}`
          .toLowerCase()
          .includes(keyword);
      });

      return {
        ...group,
        items,
      };
    }).filter((group) => group.items.length > 0);
  }, [resultItems, searchKeyword]);

  const fetchLatestSimulationOption = useCallback(async () => {
    if (!groupCode || !projectCode) {
      setSimulationOptions(buildDemoSimulationOptions(null));
      setSimulationError(
        "그룹/프로젝트 정보가 없어 결과 목록을 불러올 수 없습니다.",
      );
      return;
    }

    setSimulationLoading(true);
    setSimulationError("");

    try {
      const response = await axiosPrivate.get(
        `/StreamingAssets/${groupCode}/${projectCode}/sv/Api/ResultGraph/LatestSimulation`,
        { baseURL: rootUrl },
      );

      const latestSimulation = response?.data?.latestSimulation || null;

      setSimulationOptions(buildDemoSimulationOptions(latestSimulation));

      if (!latestSimulation) {
        setSimulationError(
          "저장된 시뮬레이션 결과가 없습니다. 데모 템플릿으로만 표시됩니다.",
        );
      }
    } catch (error) {
      setSimulationOptions(buildDemoSimulationOptions(null));
      setSimulationError(
        error?.response?.data?.message ||
          "최신 시뮬레이션 결과 정보를 불러오지 못했습니다.",
      );
    } finally {
      setSimulationLoading(false);
    }
  }, [groupCode, projectCode, rootUrl]);

  const fetchLineBalancingMetric = useCallback(async () => {
    if (!groupCode || !projectCode) {
      setLineBalancingMetric(null);
      setLineBalancingError(
        "그룹/프로젝트 정보가 없어 Line Balancing 데이터를 불러올 수 없습니다.",
      );
      return;
    }

    setLineBalancingLoading(true);
    setLineBalancingError("");

    try {
      const response = await axiosPrivate.get(
        `/StreamingAssets/${groupCode}/${projectCode}/sv/Api/ResultGraph/Metric/LineBalancing`,
        { baseURL: rootUrl },
      );

      const metricPayload = response?.data || null;
      if (!metricPayload?.success) {
        setLineBalancingMetric(null);
        setLineBalancingError(
          "Line Balancing 데이터 응답 형식이 올바르지 않습니다.",
        );
        return;
      }

      setLineBalancingMetric(metricPayload);
    } catch (error) {
      setLineBalancingMetric(null);
      setLineBalancingError(
        error?.response?.data?.message ||
          "Line Balancing 데이터를 불러오지 못했습니다.",
      );
    } finally {
      setLineBalancingLoading(false);
    }
  }, [groupCode, projectCode, rootUrl]);

  const fetchProcLeadTimeMetric = useCallback(async () => {
    if (!groupCode || !projectCode) {
      setProcLeadTimeMetric(null);
      setProcLeadTimeError(
        "그룹/프로젝트 정보가 없어 공정간 LeadTime 데이터를 불러올 수 없습니다.",
      );
      return;
    }

    setProcLeadTimeLoading(true);
    setProcLeadTimeError("");

    try {
      const response = await axiosPrivate.get(
        `/StreamingAssets/${groupCode}/${projectCode}/sv/Api/ResultGraph/Metric/ProcLeadTime`,
        { baseURL: rootUrl },
      );

      const metricPayload = response?.data || null;
      if (!metricPayload?.success) {
        setProcLeadTimeMetric(null);
        setProcLeadTimeError(
          "공정간 LeadTime 데이터 응답 형식이 올바르지 않습니다.",
        );
        return;
      }

      setProcLeadTimeMetric(metricPayload);
    } catch (error) {
      setProcLeadTimeMetric(null);
      setProcLeadTimeError(
        error?.response?.data?.message ||
          "공정간 LeadTime 데이터를 불러오지 못했습니다.",
      );
    } finally {
      setProcLeadTimeLoading(false);
    }
  }, [groupCode, projectCode, rootUrl]);

  const fetchTaktTimeMetric = useCallback(async () => {
    if (!groupCode || !projectCode) {
      setTaktTimeMetric(null);
      setTaktTimeError(
        "그룹/프로젝트 정보가 없어 Takt Time 데이터를 불러올 수 없습니다.",
      );
      return;
    }

    setTaktTimeLoading(true);
    setTaktTimeError("");

    try {
      const response = await axiosPrivate.get(
        `/StreamingAssets/${groupCode}/${projectCode}/sv/Api/ResultGraph/Metric/TaktTime`,
        {
          baseURL: rootUrl,
          params: {
            limit: 10000,
            offset: 0,
          },
        },
      );

      const payload = response?.data || null;
      if (!payload?.success || !Array.isArray(payload?.rows)) {
        setTaktTimeMetric(null);
        setTaktTimeError("Takt Time 데이터 응답 형식이 올바르지 않습니다.");
        return;
      }

      setTaktTimeMetric(payload);
    } catch (error) {
      setTaktTimeMetric(null);
      setTaktTimeError(
        error?.response?.data?.message ||
          "Takt Time 데이터를 불러오지 못했습니다.",
      );
    } finally {
      setTaktTimeLoading(false);
    }
  }, [groupCode, projectCode, rootUrl]);

  const fetchUtilizationMchMetric = useCallback(async () => {
    if (!groupCode || !projectCode) {
      setUtilizationMchMetric(null);
      setUtilizationMchError(
        "그룹/프로젝트 정보가 없어 장비 가동실적 데이터를 불러올 수 없습니다.",
      );
      return;
    }

    setUtilizationMchLoading(true);
    setUtilizationMchError("");

    try {
      const response = await axiosPrivate.get(
        `/StreamingAssets/${groupCode}/${projectCode}/sv/Api/ResultGraph/Metric/UtilizationMch`,
        {
          baseURL: rootUrl,
          params: {
            limit: 10000,
            offset: 0,
          },
        },
      );

      const payload = response?.data || null;
      if (!payload?.success || !Array.isArray(payload?.rows)) {
        setUtilizationMchMetric(null);
        setUtilizationMchError(
          "장비 가동실적 데이터 응답 형식이 올바르지 않습니다.",
        );
        return;
      }

      setUtilizationMchMetric(payload);
    } catch (error) {
      setUtilizationMchMetric(null);
      setUtilizationMchError(
        error?.response?.data?.message ||
          "장비 가동실적 데이터를 불러오지 못했습니다.",
      );
    } finally {
      setUtilizationMchLoading(false);
    }
  }, [groupCode, projectCode, rootUrl]);

  const fetchUtilizationLogisticsMetric = useCallback(async () => {
    if (!groupCode || !projectCode) {
      setUtilizationLogisticsMetric(null);
      setUtilizationLogisticsError(
        "그룹/프로젝트 정보가 없어 물류 가동실적 데이터를 불러올 수 없습니다.",
      );
      return;
    }

    setUtilizationLogisticsLoading(true);
    setUtilizationLogisticsError("");

    try {
      const response = await axiosPrivate.get(
        `/StreamingAssets/${groupCode}/${projectCode}/sv/Api/ResultGraph/Metric/UtilizationLogistics`,
        {
          baseURL: rootUrl,
          params: {
            limit: 10000,
            offset: 0,
          },
        },
      );

      const payload = response?.data || null;
      if (!payload?.success || !Array.isArray(payload?.rows)) {
        setUtilizationLogisticsMetric(null);
        setUtilizationLogisticsError(
          "물류 가동실적 데이터 응답 형식이 올바르지 않습니다.",
        );
        return;
      }

      setUtilizationLogisticsMetric(payload);
    } catch (error) {
      setUtilizationLogisticsMetric(null);
      setUtilizationLogisticsError(
        error?.response?.data?.message ||
          "물류 가동실적 데이터를 불러오지 못했습니다.",
      );
    } finally {
      setUtilizationLogisticsLoading(false);
    }
  }, [groupCode, projectCode, rootUrl]);

  const fetchUtilizationWorkerMetric = useCallback(async () => {
    if (!groupCode || !projectCode) {
      setUtilizationWorkerMetric(null);
      setUtilizationWorkerError(
        "그룹/프로젝트 정보가 없어 작업자 가동실적 데이터를 불러올 수 없습니다.",
      );
      return;
    }

    setUtilizationWorkerLoading(true);
    setUtilizationWorkerError("");

    try {
      const response = await axiosPrivate.get(
        `/StreamingAssets/${groupCode}/${projectCode}/sv/Api/ResultGraph/Metric/UtilizationWorker`,
        {
          baseURL: rootUrl,
          params: {
            limit: 10000,
            offset: 0,
          },
        },
      );

      const payload = response?.data || null;
      if (!payload?.success || !Array.isArray(payload?.rows)) {
        setUtilizationWorkerMetric(null);
        setUtilizationWorkerError(
          "작업자 가동실적 데이터 응답 형식이 올바르지 않습니다.",
        );
        return;
      }

      setUtilizationWorkerMetric(payload);
    } catch (error) {
      setUtilizationWorkerMetric(null);
      setUtilizationWorkerError(
        error?.response?.data?.message ||
          "작업자 가동실적 데이터를 불러오지 못했습니다.",
      );
    } finally {
      setUtilizationWorkerLoading(false);
    }
  }, [groupCode, projectCode, rootUrl]);

  const fetchMchBufferMetric = useCallback(async () => {
    if (!groupCode || !projectCode) {
      setMchBufferMetric(null);
      setMchBufferError(
        "그룹/프로젝트 정보가 없어 장비 Buffer 데이터를 불러올 수 없습니다.",
      );
      return;
    }

    setMchBufferLoading(true);
    setMchBufferError("");

    try {
      const response = await axiosPrivate.get(
        `/StreamingAssets/${groupCode}/${projectCode}/sv/Api/ResultGraph/Metric/MchBuffer`,
        {
          baseURL: rootUrl,
          params: {
            limit: 10000,
            offset: 0,
          },
        },
      );

      const payload = response?.data || null;
      if (!payload?.success || !Array.isArray(payload?.rows)) {
        setMchBufferMetric(null);
        setMchBufferError("장비 Buffer 데이터 응답 형식이 올바르지 않습니다.");
        return;
      }

      setMchBufferMetric(payload);
    } catch (error) {
      setMchBufferMetric(null);
      setMchBufferError(
        error?.response?.data?.message ||
          "장비 Buffer 데이터를 불러오지 못했습니다.",
      );
    } finally {
      setMchBufferLoading(false);
    }
  }, [groupCode, projectCode, rootUrl]);

  const fetchUphMetric = useCallback(async () => {
    if (!groupCode || !projectCode) {
      setUphMetric(null);
      setUphError("그룹/프로젝트 정보가 없어 UPH 데이터를 불러올 수 없습니다.");
      return;
    }

    setUphLoading(true);
    setUphError("");

    try {
      const response = await axiosPrivate.get(
        `/StreamingAssets/${groupCode}/${projectCode}/sv/Api/ResultGraph/Metric/Uph`,
        { baseURL: rootUrl },
      );

      const payload = response?.data || null;
      if (!payload?.success || !Array.isArray(payload?.series)) {
        setUphMetric(null);
        setUphError("UPH 데이터 응답 형식이 올바르지 않습니다.");
        return;
      }

      setUphMetric(payload);
    } catch (error) {
      setUphMetric(null);
      setUphError(
        error?.response?.data?.message || "UPH 데이터를 불러오지 못했습니다.",
      );
    } finally {
      setUphLoading(false);
    }
  }, [groupCode, projectCode, rootUrl]);

  const fetchLogisticsCongestionMetric = useCallback(async () => {
    if (!groupCode || !projectCode) {
      setLogisticsCongestionMetric(null);
      setLogisticsCongestionError(
        "그룹/프로젝트 정보가 없어 물류 혼잡 데이터를 불러올 수 없습니다.",
      );
      return;
    }

    setLogisticsCongestionLoading(true);
    setLogisticsCongestionError("");

    try {
      const response = await axiosPrivate.get(
        `/StreamingAssets/${groupCode}/${projectCode}/sv/Api/ResultGraph/Metric/LogisticsCongestion`,
        { baseURL: rootUrl },
      );

      const payload = response?.data || null;
      if (!payload?.success || !Array.isArray(payload?.series)) {
        setLogisticsCongestionMetric(null);
        setLogisticsCongestionError(
          "물류 혼잡 데이터 응답 형식이 올바르지 않습니다.",
        );
        return;
      }

      setLogisticsCongestionMetric(payload);
    } catch (error) {
      setLogisticsCongestionMetric(null);
      setLogisticsCongestionError(
        error?.response?.data?.message ||
          "물류 혼잡 데이터를 불러오지 못했습니다.",
      );
    } finally {
      setLogisticsCongestionLoading(false);
    }
  }, [groupCode, projectCode, rootUrl]);

  const handleToggleResultSelection = useCallback((resultId) => {
    setSelectedResultIds((prev) => {
      if (prev.includes(resultId)) {
        return prev.filter((id) => id !== resultId);
      }
      return [...prev, resultId];
    });
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSearchKeyword("");
    setSelectedResultIds([]);
    setIsCompareMode(false);
    setSimulationOptions(buildDemoSimulationOptions(null));
    setLeftSimulationOptionId(DEFAULT_LEFT_SIMULATION_OPTION_ID);
    setRightSimulationOptionId(DEFAULT_RIGHT_SIMULATION_OPTION_ID);
    setProcLeadTimeMetric(null);
    setProcLeadTimeLoading(false);
    setProcLeadTimeError("");
    setTaktTimeMetric(null);
    setTaktTimeLoading(false);
    setTaktTimeError("");
    setUtilizationMchMetric(null);
    setUtilizationMchLoading(false);
    setUtilizationMchError("");
    setUtilizationLogisticsMetric(null);
    setUtilizationLogisticsLoading(false);
    setUtilizationLogisticsError("");
    setUtilizationWorkerMetric(null);
    setUtilizationWorkerLoading(false);
    setUtilizationWorkerError("");
    setMchBufferMetric(null);
    setMchBufferLoading(false);
    setMchBufferError("");
    setLineBalancingMetric(null);
    setLineBalancingLoading(false);
    setLineBalancingError("");
    setUphMetric(null);
    setUphLoading(false);
    setUphError("");
    setLogisticsCongestionMetric(null);
    setLogisticsCongestionLoading(false);
    setLogisticsCongestionError("");

    fetchLatestSimulationOption();
    fetchProcLeadTimeMetric();
    fetchTaktTimeMetric();
    fetchUtilizationMchMetric();
    fetchUtilizationLogisticsMetric();
    fetchUtilizationWorkerMetric();
    fetchMchBufferMetric();
    fetchLineBalancingMetric();
    fetchUphMetric();
    fetchLogisticsCongestionMetric();
  }, [
    fetchLatestSimulationOption,
    fetchLineBalancingMetric,
    fetchLogisticsCongestionMetric,
    fetchMchBufferMetric,
    fetchProcLeadTimeMetric,
    fetchTaktTimeMetric,
    fetchUphMetric,
    fetchUtilizationMchMetric,
    fetchUtilizationLogisticsMetric,
    fetchUtilizationWorkerMetric,
    open,
  ]);

  useEffect(() => {
    if (
      simulationOptions.some((option) => option.id === leftSimulationOptionId)
    ) {
      return;
    }
    setLeftSimulationOptionId(DEFAULT_LEFT_SIMULATION_OPTION_ID);
  }, [leftSimulationOptionId, simulationOptions]);

  useEffect(() => {
    if (
      simulationOptions.some((option) => option.id === rightSimulationOptionId)
    ) {
      return;
    }
    setRightSimulationOptionId(DEFAULT_RIGHT_SIMULATION_OPTION_ID);
  }, [rightSimulationOptionId, simulationOptions]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[1600]">
      <button
        type="button"
        aria-label="결과 확인 팝업 닫기"
        onClick={onClose}
        className="absolute inset-0 w-full bg-black/70 backdrop-blur-[2px]"
      />

      <div className="relative mx-3 my-3 flex h-[calc(100vh-1.5rem)] w-[calc(100%-1.5rem)] flex-col overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 text-slate-100 shadow-[0_40px_100px_rgba(0,0,0,0.65)]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 px-5 py-4">
          <div>
            <h2 className="text-base font-bold tracking-tight">결과 확인</h2>
            <p className="mt-1 text-xs text-slate-400">
              최신 시뮬레이션 결과 1건 기준으로 9개 결과 지표를 탐색합니다.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="rounded-full border border-slate-700 bg-slate-900/80 px-2 py-1 text-[10px] font-semibold text-slate-300">
              저장 방식: 최신 1건
            </span>

            <button
              type="button"
              onClick={() => {
                setIsCompareMode((prev) => {
                  const nextCompareMode = !prev;
                  if (nextCompareMode) {
                    setLeftSimulationOptionId(
                      DEFAULT_LEFT_SIMULATION_OPTION_ID,
                    );
                    setRightSimulationOptionId(
                      DEFAULT_RIGHT_SIMULATION_OPTION_ID,
                    );
                  }
                  return nextCompareMode;
                });
              }}
              className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                isCompareMode
                  ? "border-cyan-400/50 bg-cyan-500/10 text-cyan-200"
                  : "border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white"
              }`}
            >
              결과 비교
            </button>

            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-300 transition-colors hover:border-slate-500 hover:text-white"
            >
              닫기
            </button>
          </div>
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:flex-row">
          <aside className="flex w-full shrink-0 flex-col border-b border-slate-800 bg-slate-900/45 lg:w-[320px] lg:border-b-0 lg:border-r">
            <div className="border-b border-slate-800 px-4 py-3">
              <p className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-400">
                결과 항목 선택
              </p>
              <p className="mt-1 text-[11px] text-slate-500">
                여러 결과 항목을 선택/해제할 수 있으며, 선택 순서대로
                표시됩니다.
              </p>

              <input
                type="text"
                value={searchKeyword}
                onChange={(event) => setSearchKeyword(event.target.value)}
                placeholder="결과명 / 테이블 검색"
                className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-100 placeholder:text-slate-500"
              />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1">
              {filteredGroups.length === 0 && (
                <div className="rounded-lg border border-slate-800 bg-slate-900/70 px-3 py-5 text-center text-xs text-slate-500">
                  검색 조건에 맞는 결과 항목이 없습니다.
                </div>
              )}

              {filteredGroups.map((group) => (
                <div key={`result-group-${group.id}`} className="mb-4">
                  <div className="px-1">
                    <div className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-400">
                      {group.label}
                    </div>
                    <div className="mt-0.5 text-[10px] text-slate-500">
                      {group.description}
                    </div>
                  </div>

                  <div className="mt-2 space-y-1.5">
                    {group.items.map((item) => {
                      const active = selectedOrderMap.has(item.id);
                      const selectedOrder = selectedOrderMap.get(item.id);

                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleToggleResultSelection(item.id)}
                          className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${
                            active
                              ? "border-indigo-400/40 bg-indigo-500/10"
                              : "border-transparent bg-slate-900/70 hover:border-slate-700 hover:bg-slate-900"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-xs font-semibold text-slate-200">
                              {item.label}
                            </div>
                            {selectedOrder && (
                              <span className="rounded-full bg-indigo-500/20 px-2 py-[2px] text-[10px] font-bold text-indigo-200">
                                {selectedOrder}
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 text-[10px] text-slate-500">
                            {item.tableName}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </aside>

          <section className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="border-b border-slate-800 px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-bold text-slate-200">
                    {isCompareMode
                      ? selectedResultItems.length > 0
                        ? `${selectedResultItems.length}개 항목 비교`
                        : "결과 비교 템플릿"
                      : selectedResultItems.length > 0
                        ? `${selectedResultItems.length}개 결과 선택됨`
                        : "결과 항목을 선택하세요"}
                  </h3>
                  <p className="mt-1 text-[11px] text-slate-500">
                    {isCompareMode
                      ? "선택된 결과 항목들을 좌/우 시뮬레이션 기준으로 리포트처럼 비교 표시합니다."
                      : "선택된 결과 항목들을 선택 순서대로 지표별 표시 방식에 맞춰 확인합니다."}
                  </p>
                </div>
              </div>

              {selectedResultItems.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {selectedResultItems.map((item, index) => (
                    <button
                      key={`selected-result-chip-${item.id}`}
                      type="button"
                      onClick={() => handleToggleResultSelection(item.id)}
                      className="rounded-full border border-indigo-500/40 bg-indigo-500/10 px-2 py-[3px] text-[10px] font-semibold text-indigo-200 hover:border-indigo-400"
                    >
                      {index + 1}. {item.label} x
                    </button>
                  ))}
                </div>
              )}

              {!isCompareMode && (
                <div style={{ marginTop: "8px" }}>
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(99,102,241,0.06) 0%, rgba(10,14,28,0.95) 100%)",
                      border: "1px solid rgba(99,102,241,0.25)",
                      borderRadius: "10px",
                      padding: "10px 12px",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: "3px",
                        background:
                          "linear-gradient(180deg, #818cf8 0%, #4f46e5 100%)",
                        borderRadius: "10px 0 0 10px",
                      }}
                    />
                    <div style={{ paddingLeft: "8px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          marginBottom: "6px",
                        }}
                      >
                        <span
                          style={{
                            width: "5px",
                            height: "5px",
                            borderRadius: "50%",
                            background: "#818cf8",
                            flexShrink: 0,
                            boxShadow: "0 0 5px #818cf870",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            color: "#a5b4fc",
                            textTransform: "uppercase",
                          }}
                        >
                          시뮬레이션 결과
                        </span>
                        {simulationLoading && (
                          <span
                            style={{
                              fontSize: "10px",
                              color: "#475569",
                              marginLeft: "4px",
                            }}
                          >
                            불러오는 중...
                          </span>
                        )}
                      </div>
                      <DarkSelectDropdown
                        value={leftSimulationOptionId}
                        onChange={setLeftSimulationOptionId}
                        options={simulationOptions.map((opt) => ({
                          value: opt.id,
                          label: opt.label,
                        }))}
                        placeholder="시뮬레이션을 선택하세요"
                        disabled={
                          simulationLoading || simulationOptions.length === 0
                        }
                        accentColor="#6366f1"
                      />
                    </div>
                  </div>
                </div>
              )}

              {isCompareMode && (
                <div
                  style={{
                    marginTop: "8px",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                  }}
                >
                  {/* 좌측 시뮬레이션 */}
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(6,182,212,0.06) 0%, rgba(10,14,28,0.95) 100%)",
                      border: "1px solid rgba(6,182,212,0.28)",
                      borderRadius: "10px",
                      padding: "10px 12px",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: "3px",
                        background:
                          "linear-gradient(180deg, #22d3ee 0%, #0891b2 100%)",
                        borderRadius: "10px 0 0 10px",
                      }}
                    />
                    <div style={{ paddingLeft: "8px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          marginBottom: "6px",
                        }}
                      >
                        <span
                          style={{
                            width: "5px",
                            height: "5px",
                            borderRadius: "50%",
                            background: "#06b6d4",
                            flexShrink: 0,
                            boxShadow: "0 0 5px #06b6d470",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            color: "#67e8f9",
                            textTransform: "uppercase",
                          }}
                        >
                          좌측 시뮬레이션
                        </span>
                      </div>
                      <DarkSelectDropdown
                        value={leftSimulationOptionId}
                        onChange={setLeftSimulationOptionId}
                        options={simulationOptions.map((opt) => ({
                          value: opt.id,
                          label: opt.label,
                        }))}
                        placeholder="시뮬레이션 선택"
                        disabled={
                          simulationLoading || simulationOptions.length === 0
                        }
                        accentColor="#06b6d4"
                      />
                    </div>
                  </div>

                  {/* 우측 시뮬레이션 */}
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(139,92,246,0.06) 0%, rgba(10,14,28,0.95) 100%)",
                      border: "1px solid rgba(139,92,246,0.28)",
                      borderRadius: "10px",
                      padding: "10px 12px",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: "3px",
                        background:
                          "linear-gradient(180deg, #a78bfa 0%, #7c3aed 100%)",
                        borderRadius: "10px 0 0 10px",
                      }}
                    />
                    <div style={{ paddingLeft: "8px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          marginBottom: "6px",
                        }}
                      >
                        <span
                          style={{
                            width: "5px",
                            height: "5px",
                            borderRadius: "50%",
                            background: "#8b5cf6",
                            flexShrink: 0,
                            boxShadow: "0 0 5px #8b5cf670",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            color: "#c4b5fd",
                            textTransform: "uppercase",
                          }}
                        >
                          우측 시뮬레이션
                        </span>
                      </div>
                      <DarkSelectDropdown
                        value={rightSimulationOptionId}
                        onChange={setRightSimulationOptionId}
                        options={simulationOptions.map((opt) => ({
                          value: opt.id,
                          label: opt.label,
                        }))}
                        placeholder="시뮬레이션 선택"
                        disabled={
                          simulationLoading || simulationOptions.length === 0
                        }
                        accentColor="#8b5cf6"
                      />
                    </div>
                  </div>
                </div>
              )}

              {(simulationLoading || simulationError) && (
                <div className="mt-2 rounded-lg border border-slate-700 bg-slate-900/70 px-3 py-2 text-[11px] text-slate-400">
                  {simulationLoading
                    ? "최신 시뮬레이션 결과 정보를 불러오는 중입니다..."
                    : simulationError}
                </div>
              )}
            </div>

            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto p-4 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5">
              {!isCompareMode && selectedResultItems.length === 0 && (
                <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/60 px-4 py-10 text-center text-sm text-slate-500">
                  좌측에서 결과 항목을 여러 개 선택하면 선택 순서대로 지표별
                  화면이 표시됩니다.
                </div>
              )}

              {!isCompareMode && selectedResultItems.length > 0 && (
                <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-2 xl:items-stretch">
                  {selectedResultItems.map((resultItem, index) => (
                    <div
                      key={`single-result-panel-${resultItem.id}`}
                      className="min-w-0"
                    >
                      <ResultPanelCard
                        panelTitle={resultItem.label}
                        resultItem={resultItem}
                        compareTemplate={false}
                        orderText={String(index + 1)}
                        utilizationMchMetric={utilizationMchMetric}
                        utilizationMchLoading={utilizationMchLoading}
                        utilizationMchError={utilizationMchError}
                        utilizationLogisticsMetric={utilizationLogisticsMetric}
                        utilizationLogisticsLoading={
                          utilizationLogisticsLoading
                        }
                        utilizationLogisticsError={utilizationLogisticsError}
                        utilizationWorkerMetric={utilizationWorkerMetric}
                        utilizationWorkerLoading={utilizationWorkerLoading}
                        utilizationWorkerError={utilizationWorkerError}
                        mchBufferMetric={mchBufferMetric}
                        mchBufferLoading={mchBufferLoading}
                        mchBufferError={mchBufferError}
                        taktTimeMetric={taktTimeMetric}
                        taktTimeLoading={taktTimeLoading}
                        taktTimeError={taktTimeError}
                        procLeadTimeMetric={procLeadTimeMetric}
                        procLeadTimeLoading={procLeadTimeLoading}
                        procLeadTimeError={procLeadTimeError}
                        lineBalancingMetric={lineBalancingMetric}
                        lineBalancingLoading={lineBalancingLoading}
                        lineBalancingError={lineBalancingError}
                        uphMetric={uphMetric}
                        uphLoading={uphLoading}
                        uphError={uphError}
                        logisticsCongestionMetric={logisticsCongestionMetric}
                        logisticsCongestionLoading={logisticsCongestionLoading}
                        logisticsCongestionError={logisticsCongestionError}
                      />
                    </div>
                  ))}
                </div>
              )}

              {isCompareMode && selectedResultItems.length === 0 && (
                <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/60 px-4 py-10 text-center text-sm text-slate-500">
                  비교할 결과 항목을 좌측 목록에서 먼저 선택해주세요. 여러 항목
                  선택 시 리포트처럼 순서대로 나열됩니다.
                </div>
              )}

              {isCompareMode && selectedResultItems.length > 0 && (
                <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-2 xl:items-stretch">
                  <div className="min-w-0 space-y-4">
                    {!leftSimulationOption && (
                      <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/60 px-4 py-10 text-center text-sm text-slate-500">
                        좌측 시뮬레이션 결과를 선택하면 선택된 결과 항목 전체가
                        표시됩니다.
                      </div>
                    )}

                    {leftSimulationOption && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "7px 12px",
                            background: "rgba(6,182,212,0.07)",
                            border: "1px solid rgba(6,182,212,0.22)",
                            borderRadius: "8px",
                          }}
                        >
                          <span
                            style={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              background: "#06b6d4",
                              flexShrink: 0,
                              boxShadow: "0 0 5px #06b6d470",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "10px",
                              fontWeight: 700,
                              color: "#67e8f9",
                              letterSpacing: "0.06em",
                              textTransform: "uppercase",
                              flexShrink: 0,
                            }}
                          >
                            좌측
                          </span>
                          <span
                            style={{
                              fontSize: "11px",
                              color: "#94a3b8",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {leftSimulationOption.label}
                          </span>
                        </div>
                        {selectedResultItems.map((resultItem, index) => (
                          <ResultPanelCard
                            key={`compare-left-${resultItem.id}`}
                            panelTitle={`좌측 · ${resultItem.label}`}
                            resultItem={resultItem}
                            compareTemplate
                            orderText={String(index + 1)}
                            utilizationMchMetric={utilizationMchMetric}
                            utilizationMchLoading={utilizationMchLoading}
                            utilizationMchError={utilizationMchError}
                            utilizationLogisticsMetric={
                              utilizationLogisticsMetric
                            }
                            utilizationLogisticsLoading={
                              utilizationLogisticsLoading
                            }
                            utilizationLogisticsError={
                              utilizationLogisticsError
                            }
                            utilizationWorkerMetric={utilizationWorkerMetric}
                            utilizationWorkerLoading={utilizationWorkerLoading}
                            utilizationWorkerError={utilizationWorkerError}
                            mchBufferMetric={mchBufferMetric}
                            mchBufferLoading={mchBufferLoading}
                            mchBufferError={mchBufferError}
                            taktTimeMetric={taktTimeMetric}
                            taktTimeLoading={taktTimeLoading}
                            taktTimeError={taktTimeError}
                            procLeadTimeMetric={procLeadTimeMetric}
                            procLeadTimeLoading={procLeadTimeLoading}
                            procLeadTimeError={procLeadTimeError}
                            lineBalancingMetric={lineBalancingMetric}
                            lineBalancingLoading={lineBalancingLoading}
                            lineBalancingError={lineBalancingError}
                            uphMetric={uphMetric}
                            uphLoading={uphLoading}
                            uphError={uphError}
                            logisticsCongestionMetric={logisticsCongestionMetric}
                            logisticsCongestionLoading={
                              logisticsCongestionLoading
                            }
                            logisticsCongestionError={
                              logisticsCongestionError
                            }
                          />
                        ))}
                      </>
                    )}
                  </div>

                  <div className="min-w-0 space-y-4">
                    {!rightSimulationOption && (
                      <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/60 px-4 py-10 text-center text-sm text-slate-500">
                        우측 시뮬레이션 결과를 선택하면 선택된 결과 항목 전체가
                        표시됩니다.
                      </div>
                    )}

                    {rightSimulationOption && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "7px 12px",
                            background: "rgba(139,92,246,0.07)",
                            border: "1px solid rgba(139,92,246,0.22)",
                            borderRadius: "8px",
                          }}
                        >
                          <span
                            style={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              background: "#8b5cf6",
                              flexShrink: 0,
                              boxShadow: "0 0 5px #8b5cf670",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "10px",
                              fontWeight: 700,
                              color: "#c4b5fd",
                              letterSpacing: "0.06em",
                              textTransform: "uppercase",
                              flexShrink: 0,
                            }}
                          >
                            우측
                          </span>
                          <span
                            style={{
                              fontSize: "11px",
                              color: "#94a3b8",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {rightSimulationOption.label}
                          </span>
                        </div>
                        {selectedResultItems.map((resultItem, index) => (
                          <ResultPanelCard
                            key={`compare-right-${resultItem.id}`}
                            panelTitle={`우측 · ${resultItem.label}`}
                            resultItem={resultItem}
                            compareTemplate
                            orderText={String(index + 1)}
                            utilizationMchMetric={utilizationMchMetric}
                            utilizationMchLoading={utilizationMchLoading}
                            utilizationMchError={utilizationMchError}
                            utilizationLogisticsMetric={
                              utilizationLogisticsMetric
                            }
                            utilizationLogisticsLoading={
                              utilizationLogisticsLoading
                            }
                            utilizationLogisticsError={
                              utilizationLogisticsError
                            }
                            utilizationWorkerMetric={utilizationWorkerMetric}
                            utilizationWorkerLoading={utilizationWorkerLoading}
                            utilizationWorkerError={utilizationWorkerError}
                            mchBufferMetric={mchBufferMetric}
                            mchBufferLoading={mchBufferLoading}
                            mchBufferError={mchBufferError}
                            taktTimeMetric={taktTimeMetric}
                            taktTimeLoading={taktTimeLoading}
                            taktTimeError={taktTimeError}
                            procLeadTimeMetric={procLeadTimeMetric}
                            procLeadTimeLoading={procLeadTimeLoading}
                            procLeadTimeError={procLeadTimeError}
                            lineBalancingMetric={lineBalancingMetric}
                            lineBalancingLoading={lineBalancingLoading}
                            lineBalancingError={lineBalancingError}
                            uphMetric={uphMetric}
                            uphLoading={uphLoading}
                            uphError={uphError}
                            logisticsCongestionMetric={logisticsCongestionMetric}
                            logisticsCongestionLoading={
                              logisticsCongestionLoading
                            }
                            logisticsCongestionError={
                              logisticsCongestionError
                            }
                          />
                        ))}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
